// =====================================================================
// Employee form — used inside the Add / Edit Employee dialogs.
// Extracted from pages/Employees.tsx without any logic changes.
// =====================================================================

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  User,
  Eye,
  EyeOff,
  FileText,
  Briefcase,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Search as SearchIcon,
  Landmark,
} from "lucide-react";
import { BANK_FIELDS, BankAttachmentsField } from "../../components/BankDetailsModal";
import {
  uploadEmployeeDocument,
  getEmployeeDocuments,
  deleteEmployeeDocument,
  type EmployeeDocType,
} from "../../api/employees";
import { useFileDropPaste } from "../../hooks/useFileDropPaste";
import { getCompanyComplianceSettings, getOffice } from "../../api/officeandcompany";
import { useNavigate } from "react-router-dom";
import {
  getManagerList,
  getDepartmentList,
  getDesignationList,
} from "../../redux/slices/employee";
import { User as UserType } from "../../types";
import { useToast } from "../../hooks/useToast";
import { country } from "../../_mock/country";
import { state } from "../../_mock/state";
import { dispatch, useSelector } from "../../redux/store";
import { listAccess, getEmployeeAccess, assignEmployeeAccess, type AccessProfile } from "../../api/access";
import {
  listKioskOptions,
  getEmployeeKiosk,
  assignEmployeeToKiosk,
  type KioskOption,
} from "../../api/attendanceKiosk";
import { useAccess } from "../../contexts/AccessContext";
import { RoleAccessQuickCreate } from "../../components/RoleAccessQuickCreate";
import api from "../../api/api";

import {
  PT_STATES,
  LWF_STATES,
  calcTDS,
  calcPT,
  lwfMonthly,
  fmt,
  num,
  capitalizeFirstLetter,
  splitCtcToComponents,
  calcFyTax,
  currentFinancialYear,
  financialYearOptions,
  fyBounds,
  monthsActiveInFy,
  type FySlice,
} from "../../utils/employee.constants";
import { listRoles, type Role } from "../../api/roles";
import { assignableRoles } from "../../utils/roleHierarchy";

// Statutory identifiers printed in the salary-slip header (see
// hrms/utils/salary_slip_pdf.py). Shaped like BANK_FIELDS so the Bank Details
// card renders both lists with the same input markup.
const STATUTORY_FIELDS = [
  { key: "pan_number", label: "PAN Number", placeholder: "ABCDE1234F", uppercase: true },
  { key: "pf_no", label: "PF No", placeholder: "KN/BNG/12345/001", uppercase: true },
  { key: "pf_uan", label: "PF UAN", placeholder: "12 digit UAN", uppercase: false },
] as const;

type DocUploadCardProps = {
  doc: { key: EmployeeDocType; label: string };
  state?: { uploading: boolean; public_url?: string | null; blob_path?: string | null };
  staged: boolean;
  uploaded: boolean;
  isImage: boolean;
  isPdf: boolean;
  pendingName?: string;
  onUpload: (file: File | null) => void;
  onDelete: () => void;
};

function DocUploadCard({
  doc, state, staged, uploaded, isImage, isPdf, pendingName, onUpload, onDelete,
}: DocUploadCardProps) {
  const { dropRef, isDragOver } = useFileDropPaste({
    onFiles: (files) => {
      // Single-file slot — take the first file from a drop or paste.
      if (files[0]) onUpload(files[0]);
    },
    disabled: !!state?.uploading,
  });

  return (
    <div
      ref={dropRef}
      tabIndex={0}
      className={`border rounded-lg p-4 bg-white hover:shadow-sm transition outline-none ${
        isDragOver
          ? "border-blue-400 ring-2 ring-blue-300 ring-offset-1 bg-blue-50/40"
          : "border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-semibold text-gray-800">{doc.label}</Label>
        {uploaded && (
          <span className="text-[10px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
            Uploaded
          </span>
        )}
        {!uploaded && staged && (
          <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
            Staged
          </span>
        )}
      </div>

      {uploaded && state?.public_url && (
        <div className="mb-3">
          {isImage ? (
            <a href={state.public_url} target="_blank" rel="noreferrer">
              <img
                src={state.public_url}
                alt={doc.label}
                className="h-32 w-full object-contain bg-gray-50 border border-gray-200 rounded"
              />
            </a>
          ) : isPdf ? (
            <a
              href={state.public_url}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center justify-center h-32 w-full bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition"
            >
              <span className="text-red-600 text-2xl font-bold">PDF</span>
              <span className="text-xs text-gray-600 mt-1 truncate max-w-full px-2">
                {state.blob_path?.split("/").pop()}
              </span>
            </a>
          ) : (
            <a
              href={state.public_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center h-32 w-full bg-gray-50 border border-gray-200 rounded text-xs text-gray-600 hover:bg-gray-100 transition"
            >
              {state.blob_path?.split("/").pop() || "Open file"}
            </a>
          )}
        </div>
      )}

      <Input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        disabled={state?.uploading}
        onChange={(e) => onUpload(e.target.files?.[0] || null)}
        className="text-xs"
      />
      <p className="text-[10px] text-gray-500 mt-1">
        Drag &amp; drop, click to focus then paste (Ctrl/Cmd+V), or browse. Max size: 1 MB
      </p>

      <div className="flex items-center gap-3 mt-3 min-h-[20px] text-xs">
        {state?.uploading && <span className="text-blue-600">Uploading…</span>}
        {staged && (
          <span className="text-amber-700">{pendingName} — uploads after Save</span>
        )}
        {uploaded && state?.public_url && (
          <a
            href={state.public_url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline"
          >
            View
          </a>
        )}
        {(uploaded || staged) && (
          <button
            type="button"
            onClick={onDelete}
            className="text-red-600 hover:underline"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
export type EmployeeStep = "personal" | "salary" | "documents" | "summary";

const EMP_STEP_LABEL: Record<EmployeeStep, string> = {
  personal: "Personal Details",
  salary: "Salary & Deductions",
  documents: "Upload Documents",
  summary: "Review & Submit",
};

// A feature sub-permission defaults to GRANTED unless explicitly turned off.
function featureAllowed(
  hasAccess: boolean,
  permissions: Record<string, { visible?: boolean }>,
  key: string
): boolean {
  if (!hasAccess) return true;
  const p = permissions[key];
  return p === undefined ? true : p.visible === true;
}

// Ordered list of form steps this user may see. Personal & Review are always
// present (required to create/save); Salary/Documents are gated by the access
// profile's emp-salary / emp-documents sub-permissions.
function allowedEmployeeSteps(
  hasAccess: boolean,
  permissions: Record<string, { visible?: boolean }>
): EmployeeStep[] {
  const steps: EmployeeStep[] = ["personal"];
  if (featureAllowed(hasAccess, permissions, "emp-salary")) steps.push("salary");
  if (featureAllowed(hasAccess, permissions, "emp-documents")) steps.push("documents");
  steps.push("summary");
  return steps;
}

export function EmployeeStepsTabNav({
  activeTab,
  onTabChange,
  tabsLocked = false,
  unlockedTabs,
  className = "",
}: {
  activeTab: EmployeeStep;
  onTabChange: (next: EmployeeStep) => void;
  tabsLocked?: boolean;
  unlockedTabs?: EmployeeStep[];
  className?: string;
}) {
  const { hasAccess, permissions } = useAccess();
  const steps = allowedEmployeeSteps(hasAccess, permissions).map((id, i) => ({
    id,
    n: i + 1,
    label: EMP_STEP_LABEL[id],
  }));
  // Per-tab gating. An explicit `unlockedTabs` list means step-by-step control
  // (Add mode); without it, tabs are freely navigable unless globally locked.
  const hasPerTabControl = Array.isArray(unlockedTabs);
  const isUnlocked = (id: EmployeeStep) =>
    !tabsLocked ? true : hasPerTabControl ? (unlockedTabs as EmployeeStep[]).includes(id) : false;
  return (
    <div className={`flex border-b border-gray-200 ${className}`}>
      {steps.map((s) => {
        const unlocked = isUnlocked(s.id);
        const locked = !unlocked;
        return (
          <button
            key={s.id}
            type="button"
            disabled={locked}
            aria-disabled={locked || undefined}
            title={
              locked
                ? hasPerTabControl
                  ? "Complete the previous step to unlock this tab"
                  : "Use the Back/Next buttons to move between steps"
                : undefined
            }
            onClick={() => {
              if (locked) return;
              onTabChange(s.id);
            }}
            className={`px-6 py-3 text-sm font-medium transition-all ${activeTab === s.id
              ? "border-b-2 border-blue-500 text-blue-600"
              : locked
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-500 hover:text-gray-700"
              }`}
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs">{s.n}</span>
              {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function EmployeeForm({
  employee,
  onSubmit,
  currentUserRole,
  initialTab,
  activeTab: controlledActiveTab,
  onActiveTabChange,
  hideTabNav = false,
  existingEmails,
  existingPhones,
  onUnlockedTabsChange,
  financialYear: controlledFinancialYear,
  onFinancialYearChange,
}: {
  employee?: UserType;
  onSubmit: (data: Partial<UserType>) => void;
  currentUserRole: string;
  initialTab?: EmployeeStep;
  activeTab?: EmployeeStep;
  onActiveTabChange?: (next: EmployeeStep) => void;
  // Hide the form's own tab bar when the parent renders an external one.
  hideTabNav?: boolean;
  existingEmails?: string[];
  existingPhones?: string[];
  onUnlockedTabsChange?: (tabs: EmployeeStep[]) => void;
  // Financial year is selected in the dialog header; the parent controls it.
  financialYear?: string;
  onFinancialYearChange?: (fy: string) => void;
}) {
  const [employeeSettings, setSettings] = useState({
    disable_mandatory: false,
    is_active: true,
  });

  const [addDepartmentModal, setAddDepartmentModal] = useState(false);
  const [departmentName, setDepartmentName] = useState("");
  const [officeLocationMissingOpen, setOfficeLocationMissingOpen] =
    useState(false);
  const navigate = useNavigate();
  const officeHasGeofence = async (): Promise<boolean> => {
    try {
      const r = await getOffice();
      return (r?.offices || []).some((office: any) => {
        const lat = parseFloat(office.lattitude ?? office.latitude ?? "");
        const lon = parseFloat(office.longitude ?? "");
        const radius = parseFloat(office.radius_meters ?? "");
        return Number.isFinite(lat) && lat !== 0 &&
          Number.isFinite(lon) && lon !== 0 &&
          Number.isFinite(radius) && radius > 0;
      });
    } catch {
      return false;
    }
  };
  // Search input state for the in-dropdown filter on Department / Designation pickers.
  const [deptSearch, setDeptSearch] = useState("");
  const [desigSearch, setDesigSearch] = useState("");
  // Search input state for the in-dropdown filter on State (current / permanent address).
  const [currStateSearch, setCurrStateSearch] = useState("");
  const [permStateSearch, setPermStateSearch] = useState("");
  // Search input state for the in-dropdown filter on Country.
  const [countrySearch, setCountrySearch] = useState("");
  const [internalActiveTab, setInternalActiveTab] = useState<EmployeeStep>(initialTab || "personal");
  const isTabControlled =
    controlledActiveTab !== undefined && onActiveTabChange !== undefined;
  const activeTab: EmployeeStep = isTabControlled
    ? (controlledActiveTab as EmployeeStep)
    : internalActiveTab;
  const setActiveTab = (next: EmployeeStep) => {
    if (isTabControlled) {
      onActiveTabChange!(next);
    } else {
      setInternalActiveTab(next);
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── Optional employee documents (Aadhar/PAN/CV/Relieving/Form16/Experience) ───
  // Stored on GCS (bucket: teamlio). None of these are required to submit the form.
  const [docsState, setDocsState] = useState<
    Record<EmployeeDocType, { uploading: boolean; public_url?: string | null; blob_path?: string | null }>
  >({
    aadhar:            { uploading: false },
    pan:               { uploading: false },
    cv:                { uploading: false },
    relieving_letter:  { uploading: false },
    form16:            { uploading: false },
    experience_letter: { uploading: false },
  });

  // Files chosen in Add mode (employee not yet created). Uploaded automatically
  // after the employee is saved.
  const [pendingDocs, setPendingDocs] = useState<Partial<Record<EmployeeDocType, File>>>({});

  useEffect(() => {
    if (!employee?.id) return;
    let mounted = true;
    (async () => {
      try {
        const res = await getEmployeeDocuments(employee.id);
        if (!mounted) return;
        const data = res?.data || {};
        setDocsState((prev) => {
          const next = { ...prev };
          (Object.keys(next) as EmployeeDocType[]).forEach((k) => {
            if (data[k]) {
              next[k] = {
                uploading: false,
                public_url: data[k].public_url,
                blob_path: data[k].blob_path,
              };
            }
          });
          return next;
        });
      } catch (err) {
        // documents are optional — fail silently
        console.warn("Could not load employee documents", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [employee?.id]);

  const handleUploadDoc = async (docType: EmployeeDocType, file: File | null) => {
    if (!file) return;

    // Client-side size guard — backend enforces the same 1 MB cap.
    const EMPLOYEE_DOC_MAX_BYTES = 1 * 1024 * 1024;
    if (file.size > EMPLOYEE_DOC_MAX_BYTES) {
      toast({
        title: "File too large",
        description: `${docType.replace("_", " ")} must be 1 MB or smaller.`,
        variant: "destructive",
      });
      return;
    }

    // Add mode → just stage the file, upload after the employee is saved.
    if (!employee?.id) {
      setPendingDocs((prev) => ({ ...prev, [docType]: file }));
      toast({
        title: "Staged",
        description: `${docType.replace("_", " ")} will upload after you save the employee.`,
      });
      return;
    }

    setDocsState((prev) => ({ ...prev, [docType]: { ...prev[docType], uploading: true } }));
    try {
      const res = await uploadEmployeeDocument(employee.id, docType, file);
      setDocsState((prev) => ({
        ...prev,
        [docType]: {
          uploading: false,
          public_url: res?.data?.public_url,
          blob_path: res?.data?.blob_path,
        },
      }));
      toast({ title: "Uploaded", description: `${docType.replace("_", " ")} saved.` });
    } catch (err: any) {
      setDocsState((prev) => ({ ...prev, [docType]: { ...prev[docType], uploading: false } }));
      toast({
        title: "Upload failed",
        description: err?.response?.data?.detail || err?.message || "Could not upload",
        variant: "destructive",
      });
    }
  };

  const handleDeleteDoc = async (docType: EmployeeDocType) => {
    // Add mode — only the staged file exists; just clear it.
    if (!employee?.id) {
      setPendingDocs((prev) => {
        const next = { ...prev };
        delete next[docType];
        return next;
      });
      return;
    }
    try {
      await deleteEmployeeDocument(employee.id, docType);
      setDocsState((prev) => ({
        ...prev,
        [docType]: { uploading: false, public_url: null, blob_path: null },
      }));
      toast({ title: "Removed", description: `${docType.replace("_", " ")} deleted.` });
    } catch (err: any) {
      toast({
        title: "Delete failed",
        description: err?.response?.data?.detail || err?.message || "Could not delete",
        variant: "destructive",
      });
    }
  };
  const [addDesignationModal, setAddDesignationModal] = useState(false);
  const [designationName, setDesignationName] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Salary calculation states
  const [ctcEnabled, setCtcEnabled] = useState(true);
  const [ctcValue, setCtcValue] = useState(0);
  // Expand the salary-component cards by default when the employee already has
  // more than one component, so the previous LOCKED component(s) are visible
  // next to the most recent (editable) one — matching the employee page.
  const [showSalaryDetails, setShowSalaryDetails] = useState<boolean>(
    () => (employee?.SalaryDetails?.length || 0) > 1
  );
  // Older (read-only) salary components are collapsed by default; this set
  // holds the ids the admin has expanded to view.
  const [expandedRecordIds, setExpandedRecordIds] = useState<Set<string>>(new Set());
  const toggleExpandedRecord = (id: string) =>
    setExpandedRecordIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const skipCtcAutoSplitRef = useRef(true);
  const [isYearly, setIsYearly] = useState(true);
  const [ptState, setPtState] = useState(employee?.ptState || "karnataka");
  const [lwfState, setLwfState] = useState(employee?.lwfState || "karnataka");
  const [ptApplicable, setPtApplicable] = useState(false);
  const [lwfApplicable, setLwfApplicable] = useState(false);
  const [pfApplicable, setPfApplicable] = useState(false);
  // Whether the employee has opted out of EPF (allowed above the ₹15,000 wage
  // ceiling). Read-only on the admin side — shown for visibility.
  const [pfOptOut, setPfOptOut] = useState(false);
  // Whether the employee has saved & FINAL-locked their declaration. There is
  // no admin unlock — this only drives a read-only "saved & final" banner.
  const [declarationLocked, setDeclarationLocked] = useState(false);
  const [pfBasis, setPfBasis] = useState<"statutory" | "actual">(employee?.pfBasis || "statutory");
  // Employee PF rate (≥12%, employer fixed 12%) and whether employer PF is
  // part of CTC — both sourced from company compliance settings.
  const [pfEmployeeRate, setPfEmployeeRate] = useState<number>((employee as any)?.pfEmployeeRate || 12);
  // Whether the employee has locked their PF rate for this financial year.
  // Once locked it applies to the WHOLE year — every salary component (incl.
  // ones added later) keeps that rate, and it is read-only on the admin side.
  const [pfRateLocked, setPfRateLocked] = useState(false);
  const [pfFromCtc, setPfFromCtc] = useState(false);
  const [esicApplicable, setEsicApplicable] = useState(false);

  // Retainer-ship
  const [retainerEnabled, setRetainerEnabled] = useState(false);
  const [retainerAmount, setRetainerAmount] = useState(0);
  const [retainerPeriod, setRetainerPeriod] = useState(3);
  const [retainerRecurring, setRetainerRecurring] = useState(false);
  const [retainerFloor, setRetainerFloor] = useState(0);
  const [retainerStart, setRetainerStart] = useState(() => new Date().toISOString().slice(0, 7)); // "YYYY-MM"
  // False until the fields above reflect what is actually stored. The form is
  // NOT allowed to submit a retainer it never read — an empty card would save
  // as enabled:false/amount:0 and silently cancel the employee's retainer-ship.
  const [retainerHydrated, setRetainerHydrated] = useState(false);

  // TDS Declaration state
  const [declaration, setDeclaration] = useState({
    sec80c: employee?.sec80c || 0,
    sec80d: employee?.sec80d || 0,
    sec80ccd1b: employee?.sec80ccd1b || 0,
    hraExempt: employee?.hraExempt || 0,
    homeLoan: employee?.homeLoan || 0,
    ltaExempt: employee?.ltaExempt || 0,
    sec80g: employee?.sec80g || 0,
    sec80e: employee?.sec80e || 0,
    sec80u: employee?.sec80u || 0,
    npsEmployer: employee?.npsEmployer || 0,
    ptPaid: employee?.ptPaid || 0,
    otherDed: employee?.otherDed || 0,
  });

  // Financial year this salary setup belongs to. Controlled by the dialog
  // header when the parent passes it; otherwise local (defaults to the current
  // financial year).
  const [internalFinancialYear, setInternalFinancialYear] = useState<string>(() => {
    // Default to the FY of the MOST RECENT component, derived from its effective
    // date (so it matches fyOfDetail — the stored financial_year can be stale or
    // in a different order). This guarantees that FY's components ALL load,
    // including the older locked ones, instead of an empty/blank form.
    const sd: any[] = employee?.SalaryDetails || [];
    if (!sd.length) return currentFinancialYear();
    const latest = [...sd].sort((a, b) => {
      const da = new Date(a?.effective_date || a?.effectiveDate || a?.date || 0).getTime();
      const db = new Date(b?.effective_date || b?.effectiveDate || b?.date || 0).getTime();
      return db - da;
    })[0];
    const raw = latest?.effective_date || latest?.effectiveDate || latest?.date;
    return raw
      ? currentFinancialYear(new Date(raw))
      : (latest?.financial_year || currentFinancialYear());
  });
  const fyControlled = controlledFinancialYear !== undefined && !!onFinancialYearChange;
  const financialYear = fyControlled ? (controlledFinancialYear as string) : internalFinancialYear;
  // The financial year a stored salary detail belongs to. The Indian FY is
  // fixed by the component's EFFECTIVE DATE (1-Apr → 31-Mar), not by whatever
  // FY filter happened to be active when it was saved — so derive from the
  // date first and fall back to a stored financial_year only when there's no
  // usable date. (Mirrors SalaryTaxDeclaration.fyOfRec.)
  const fyOfDetail = (d: any): string => {
    const raw = d?.effective_date || d?.effectiveDate || d?.date;
    if (raw) return currentFinancialYear(new Date(raw));
    if (d?.financial_year) return String(d.financial_year);
    return financialYear;
  };
  const setFinancialYear = useCallback(
    (v: string) => {
      if (onFinancialYearChange) onFinancialYearChange(v);
      else setInternalFinancialYear(v);
    },
    [onFinancialYearChange]
  );
  // Previous-employer (Form-12B) figures are no longer collected on the form.
  const prevEmployerIncome = 0;
  const prevEmployerTds = 0;

  // Multiple salary records state. Each record carries its OWN annual CTC so a
  // mid-year revision is just a new component with a later effective date.
  const [salaryRecords, setSalaryRecords] = useState<any[]>([
    {
      id: Date.now().toString(),
      effectiveDate: employee?.hire_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      status: 'active',
      ctc: 0,
      basicSalary: 0,
      hra: 0,
      conveyance: 0,
      medicalallowance: 0,
      mealAllowance: 0,
      mealAllowanceType: 'cash',
      allowance: 0,
      lta: 0,
      others: 0,
      bonusAmount: 0,
      customComponents: [],
      isCurrent: true
    }
  ]);

  const [formData, setFormData] = useState({
    id: employee?.id || "",
    first_name: employee?.first_name || "",
    middle_name: employee?.middle_name || "",
    last_name: employee?.last_name || "",
    gender: employee?.gender || "",
    employeeID: employee?.employeeID || "",
    email: employee?.email || "",
    // Pre-fill with the existing password (the Employee table keeps a
    // readable copy and /employee_by_ID returns it) so the edit panel shows
    // it as dots and the eye toggle can reveal it. Blank only on add.
    password: employee?.password || "",
    phone: employee?.phone || "",
    date_of_birth: employee?.date_of_birth || "",
    hire_date: employee?.hire_date || "",
    department_id: employee?.department_id || "",
    // designation_id: employee?.designation_id || "",
    designation_id: "",
    manager_id: employee?.manager_id || "",
    emergency_contacts: employee?.emergency_contacts || "",
    salary_amount: employee?.salary_amount || 0,
    profile_photo_url: employee?.profile_photo_url || "",
    country: employee?.country || "19",
    current_address1: employee?.current_address1 || "",
    current_address2: employee?.current_address2 || "",
    current_city: employee?.current_city || "",
    current_state: employee?.current_state || "",
    current_pin: employee?.current_pin || "",
    permanent_address1: employee?.permanent_address1 || "",
    permanent_address2: employee?.permanent_address2 || "",
    permanent_city: employee?.permanent_city || "",
    permanent_state: employee?.permanent_state || "",
    permanent_pin: employee?.permanent_pin || "",
    employment_status: employee?.employment_status || "",
    // Statutory IDs printed on the salary slip header.
    pan_number: employee?.pan_number || "",
    pf_no: employee?.pf_no || "",
    pf_uan: employee?.pf_uan || "",
    sameAs: employee?.sameAs || false,
    accountNum: employee?.accountNum || "",
    accountType: employee?.accountType || "",
    ifscCode: employee?.ifscCode || "",
    bankName: employee?.bankName || "",
    bankbranch: employee?.bankbranch || "",
    swiftCode: employee?.swiftCode || "",
    upiCode: employee?.upiCode || "",
    qrcodeUrl: employee?.qrcodeUrl || "",
    othersBankDetails: employee?.othersBankDetails || "",
    bankAttachments: employee?.bankAttachments || [],
    is_active: employee?.is_active ?? true,
    department: employee?.department || "",
    role: employee?.role || "employee",
    netsalary: employee?.netsalary || 0,
    leave: employee?.leave || 0,
    modeOfWorking: employee?.modeOfWorking || "",
    workfromhome: employee?.workfromhome || false,
    workfromhome_list: employee?.workfromhome_list || [],
    taxRegime: employee?.taxRegime || "new",
    tdsApplicable: false,
    TaxConfig: employee?.TaxConfig || [],
    access_id: "",

  });

  // On Add, the password auto-fills with the backend default so the admin can
  // see/share it. Mandatory-disabled mode uses the simple shared "12345678";
  // otherwise it's <FirstName>@<last4 phone>. Once the admin types in the field
  // we stop overwriting it. (Edit keeps the existing value.)
  const [passwordTouched, setPasswordTouched] = useState(false);
  useEffect(() => {
    if (employee?.id || passwordTouched) return;
    let next: string;
    if (employeeSettings.disable_mandatory) {
      next = "12345678";
    } else {
      const name = (formData.first_name || "").trim().split(/\s+/)[0] || "";
      const digits = (formData.phone || "").replace(/\D/g, "");
      const last4 = digits.length >= 4 ? digits.slice(-4) : digits;
      next = name || last4 ? `${name || "user"}@${last4 || "0000"}` : "";
    }
    setFormData((prev) => (prev.password === next ? prev : { ...prev, password: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.first_name, formData.phone, passwordTouched, employee?.id, employeeSettings.disable_mandatory]);

  // Roles: fetched from /roles API for the Add/Edit role dropdown.
  const [roleOptions, setRoleOptions] = useState<Role[]>([]);
  const [showNewRole, setShowNewRole] = useState(false);
  const [showNewAccess, setShowNewAccess] = useState(false);

  useEffect(() => {
    let alive = true;
    listRoles().then((rs) => alive && setRoleOptions(rs)).catch(() => alive && setRoleOptions([]));
    return () => { alive = false; };
  }, []);

  // Access Management: list of profiles for the dropdown + the employee's
  // current assignment (loaded in edit mode).
  const [accessList, setAccessList] = useState<AccessProfile[]>([]);

  // Shared attendance kiosk (Settings -> Attendance Kiosk). Kept OUT of
  // formData on purpose: it is not an employees column, it is a row in the
  // kiosk membership table, saved by its own call after submit — exactly like
  // the Access profile above it. `savedKioskId` is what the server currently
  // holds, so an unchanged dropdown makes no write (and so a user with only
  // View on kiosks never trips the 403).
  const [kioskOptions, setKioskOptions] = useState<KioskOption[]>([]);
  const [kioskId, setKioskId] = useState<string>("");
  const [savedKioskId, setSavedKioskId] = useState<string>("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await listKioskOptions();
        if (alive) setKioskOptions(rows);
      } catch {
        // 403 for anyone without the kiosk panel, or no kiosks configured —
        // either way the field simply does not render.
      }
      if (employee?.id) {
        try {
          const current = await getEmployeeKiosk(employee.id);
          if (alive) {
            setKioskId(current || "");
            setSavedKioskId(current || "");
          }
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [employee?.id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await listAccess();
        if (alive) setAccessList(rows);
      } catch {
        /* non-fatal: dropdown just stays empty */
      }
      if (employee?.id) {
        try {
          const a = await getEmployeeAccess(employee.id);
          if (alive && a?.access_id) {
            setFormData((prev) => ({ ...prev, access_id: a.access_id as string }));
          }
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [employee?.id]);

  const isMandatoryDisabled = employeeSettings.disable_mandatory;

  useEffect(() => {
    fetchEmployeeSettings();
  }, []);


  const fetchEmployeeSettings = async () => {
    try {
      const res = await api.get("/get_employee_setting");
      // setEmployeeSettings(res.data);
      setSettings({
        disable_mandatory: res.data?.employee_setting?.disable_mandatory ?? false,
        is_active: res.data?.employee_setting?.is_active ?? true,
      });
      console.log("Employee Settings:", res.data);
    } catch (err) {
      console.error("Error fetching employee settings", err);
    }
  };

  // Load existing salary records from employee — filtered to the selected
  // financial year. Switching the FY dropdown re-runs this so the form shows
  // only that year's components; other years stay on the employee record and
  // are preserved untouched on save (see preservedDetails in handleSubmit).
  useEffect(() => {
    if (!employee?.SalaryDetails || employee.SalaryDetails.length === 0) return;

    // Only this financial year's components are shown/edited.
    const fyDetails = employee.SalaryDetails.filter(
      (d: any) => fyOfDetail(d) === financialYear
    );

    // No saved setup for the selected year → start a blank component the admin
    // can fill in to create that year's salary, without touching other years.
    if (fyDetails.length === 0) {
      skipCtcAutoSplitRef.current = true;
      setSalaryRecords([
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          detailId: '',
          effectiveDate: `${financialYear.split('-')[0]}-04-01`,
          status: 'active',
          ctc: 0,
          basicSalary: 0,
          hra: 0,
          conveyance: 0,
          medicalallowance: 0,
          mealAllowance: 0,
          mealAllowanceType: 'cash',
          allowance: 0,
          lta: 0,
          others: 0,
          bonusAmount: 0,
          customComponents: [],
          pfApplicable: true,
          isCurrent: true,
        },
      ]);
      setPfRateLocked(false);
      setCtcValue(0);
      setIsYearly(true);
      return;
    }

    const records = fyDetails.map((detail: any, index: number) => ({
      id: Date.now().toString() + index,
      // The stored row id. The API returns the components UNORDERED, so this is
      // the tie-breaker used below (and by the employee's declaration page) when
      // two components share an effective date — without it the two screens can
      // disagree about which component is the newest/editable one.
      detailId: String(detail.id ?? ''),
      // Stored as snake_case effective_date now; keep the old camelCase and
      // `date` fallbacks so records saved before this change still load.
      effectiveDate: (detail.effective_date || detail.effectiveDate || detail.date || '')?.toString().split('T')[0]
        || employee.hire_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      status: detail.status || (index === 0 ? 'active' : 'pending'),
      ctc: Number(detail.ctc) || 0,
      basicSalary: detail.basicsalary || 0,
      hra: detail.hra || 0,
      conveyance: detail.conveyance || 0,
      medicalallowance: detail.medicalallowance || 0,
      mealAllowance: detail.mealallowance || 0,
      mealAllowanceType: (detail.meal_allowance_type ?? detail.mealAllowanceType) === 'voucher' ? 'voucher' : 'cash',
      allowance: detail.allowance || 0,
      lta: detail.lta || 0,
      others: detail.others || 0,
      bonusAmount: detail.bonus || 0,
      customComponents: detail.customComponents || [],
      // Per-component EPF flag; defaults ON for legacy rows without the column.
      pfApplicable: (detail.pf_applicable ?? detail.pfApplicable) !== false,
      // Per-component declaration lock set by the employee's "Save Declaration".
      // The admin form preserves it (read-only badge); it never edits it.
      declarationLocked: !!(detail.declaration_locked ?? detail.declarationLocked),
      // Per-component PF rate + rate lock — preserved so an admin save never
      // overwrites one component's PF % with another's (each keeps its own).
      pfEmployeeRate: Number(detail.pf_employee_rate) || Number(detail.pfEmployeeRate) || 0,
      pfRateLocked: !!(detail.pf_rate_locked ?? detail.pfRateLocked),
      isCurrent: index === 0
    }))
    // Newest (latest effective date) first — the most recent component is the
    // editable one shown on top; older ones are read-only below it. Equal
    // effective dates fall back to the row id so the ordering is deterministic
    // and matches the employee's declaration page (which sorts the same way,
    // ascending); relying on the API's row order made the two screens pick
    // different "current" components.
    .sort((a: any, b: any) => {
      const byDate = new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime();
      return byDate !== 0 ? byDate : b.detailId.localeCompare(a.detailId);
    });
    if (records.length > 0) {
      // PF is a per-financial-year choice: if the employee locked it on any
      // component of THIS year, carry that rate + lock here so it applies to
      // every component and is read-only on the admin side; otherwise reset.
      const lockedDetail = fyDetails.find((d: any) => d.pf_rate_locked ?? d.pfRateLocked);
      if (lockedDetail) {
        setPfRateLocked(true);
        const lockedRate = Number(lockedDetail.pf_employee_rate) || Number(lockedDetail.pfEmployeeRate);
        if (lockedRate >= 12) setPfEmployeeRate(lockedRate);
      } else {
        setPfRateLocked(false);
      }

      skipCtcAutoSplitRef.current = true;
      const firstRecord = records[0];
      // CTC is the admin's fixed value: show the stored figure verbatim,
      // never recompute it from components (that drift caused 9,99,996).
      // Fall back to reconstruction only for old records saved before the
      // CTC was persisted. Use the newest record (records[0] after sorting).
      const storedCtc = Number(firstRecord?.ctc);
      if (storedCtc > 0) {
        // Bulk Excel imports may carry the calculated CTC while leaving the
        // optional component columns blank. Hydrate those components from the
        // stored annual CTC immediately, exactly as a manual CTC edit does, so
        // Monthly CTC and Net Pay are populated on the first render.
        const componentTotal = [
          firstRecord.basicSalary,
          firstRecord.hra,
          firstRecord.conveyance,
          firstRecord.medicalallowance,
          firstRecord.mealAllowance,
          firstRecord.allowance,
          firstRecord.lta,
          firstRecord.others,
        ].reduce((sum, value) => sum + (Number(value) || 0), 0)
          + (firstRecord.customComponents || []).reduce(
            (sum: number, component: any) => sum + (Number(component.amount) || 0),
            0,
          );

        if (componentTotal <= 0) {
          const split = splitCtcToComponents(storedCtc / 12);
          records[0] = {
            ...firstRecord,
            basicSalary: split.basic,
            hra: split.hra,
            conveyance: split.conveyance,
            medicalallowance: split.medicalallowance,
            mealAllowance: split.mealAllowance,
            allowance: split.allowance,
            lta: split.lta,
            others: split.others,
            bonusAmount: split.bonusAmount,
          };
        }
        setCtcValue(Math.round(storedCtc));
        setIsYearly(true);
      } else {
        const details = calculateSalaryDetailsForRecord(firstRecord);
        setCtcValue(Math.round(details.ctc * 12));
      }
      setSalaryRecords(records);
    }
  }, [employee, financialYear]);

  // Update salary record function
  const updateSalaryRecord = useCallback((id: string, field: string, value: any) => {
    setSalaryRecords(prevRecords =>
      prevRecords.map(record =>
        record.id === id ? { ...record, [field]: value } : record
      )
    );
  }, []);

  useEffect(() => {
    if (employee) return; // edit mode → leave existing dates alone
    if (!formData.hire_date) return;
    setSalaryRecords(prev => {
      if (prev.length === 0) return prev;
      
      const [first, ...rest] = prev;
      if (first.effectiveDate === formData.hire_date) return prev;
      return [{ ...first, effectiveDate: formData.hire_date }, ...rest];
    });
  }, [employee, formData.hire_date]);

  // Add new salary record. The new component becomes the most recent one
  // (prepended → shown on top, editable); all previous components drop to
  // read-only below it. The compact CTC box (which edits salaryRecords[0])
  // now reflects this fresh component, so reset it for the admin to fill.
  const addSalaryRecord = () => {
    skipCtcAutoSplitRef.current = true;
    // Reveal the component cards so the new (editable) component and the now
    // read-only previous one(s) are both visible.
    setShowSalaryDetails(true);
    setCtcValue(0);
    setSalaryRecords([
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        detailId: '',
        effectiveDate: new Date().toISOString().split('T')[0],
        status: 'active',
        ctc: 0,
        basicSalary: 0,
        hra: 0,
        conveyance: 0,
        medicalallowance: 0,
        mealAllowance: 0,
        mealAllowanceType: 'cash',
        allowance: 0,
        lta: 0,
        others: 0,
        bonusAmount: 0,
        customComponents: [],
        pfApplicable: true,
        isCurrent: true
      },
      ...salaryRecords.map(r => ({ ...r, isCurrent: false })),
    ]);
    // The new component is prepended (shown on top), so bring the form back to
    // the top where it's now editable instead of leaving the user at the button.
    scrollToTopOfPage();
  };

  // Remove salary record
  const removeSalaryRecord = (id: string) => {
    if (salaryRecords.length === 1) {
      toast({ // title: "Error",
        description: "At least one salary record is required", variant: "destructive"
      });
      return;
    }
    setSalaryRecords(salaryRecords.filter(record => record.id !== id));
  };

  // Calculate salary details for a specific record
  const calculateSalaryDetailsForRecord = useCallback((record: any) => {
    // Interns bypass ALL statutory compliance (PF / ESIC / PT / LWF) and income
    // tax (TDS): the stipend is paid in full, net = gross. Mirrors the
    // employee-side declaration page.
    const isIntern = (formData.role || "").toString().trim().toLowerCase() === "intern";
    const basic = num(record.basicSalary);
    const hra = num(record.hra);
    const conv = num(record.conveyance);
    const med = num(record.medicalallowance);
    const meal = num(record.mealAllowance);
    const special = num(record.allowance);
    const lta = num(record.lta);
    const other = num(record.others);

    const customTotal = record.customComponents?.reduce((sum: number, comp: any) => sum + (comp.amount || 0), 0) || 0;
    // Bonus/Incentive is not a salary component — it's added per month from
    // Payroll Records and shown separately on the payslip.
    const gross = basic + hra + conv + med + meal + special + lta + other + customTotal;

    let pfBase = pfBasis === "statutory" ? Math.min(basic, 15000) : basic;
    // PF is decided PER COMPONENT: the establishment must run EPF, and then EPF
    // is mandatory at/below the ₹15,000 basic ceiling, otherwise this
    // component's own pfApplicable flag decides. A component with EPF off cuts
    // no PF for the months it is in force.
    const pfOn = pfApplicable && !isIntern && (basic <= 15000 ? true : record.pfApplicable !== false);
    // Employer fixed 12%; employee can raise their rate (never below 12%). PF rate
    // is PER COMPONENT — use this component's own rate so each component's net is
    // computed with the rate it was declared at (fallback to the form rate for a
    // brand-new component that has no stored rate yet).
    const recRate = Math.max(12, Number(record.pfEmployeeRate) || pfEmployeeRate || 12);
    const pfEE = pfOn ? Math.round(pfBase * (recRate / 100)) : 0;
    const pfER = pfOn ? Math.round(pfBase * 0.12) : 0;
    const eps = pfOn ? Math.round(Math.min(pfBase, 15000) * 0.0833) : 0;
    const edli = pfOn ? Math.round(Math.min(pfBase, 15000) * 0.005) : 0;

    const esicEE = (esicApplicable && !isIntern && gross <= 21000) ? Math.round(gross * 0.0075) : 0;
    const esicER = (esicApplicable && !isIntern && gross <= 21000) ? Math.round(gross * 0.0325) : 0;

    const pt = ptApplicable && !isIntern ? calcPT(gross, ptState) : 0;

    const lwfData = lwfMonthly(lwfState);
    const lwfEE = lwfApplicable && !isIntern ? Math.round(lwfData.ee) : 0;
    const lwfER = lwfApplicable && !isIntern ? Math.round(lwfData.er) : 0;

    const grossAnnual = gross * 12;
    const pfEEAnnual = pfEE * 12;
    const pfERAnnual = pfER * 12;
    // Taxable income: old regime subtracts both employee + employer PF; the new
    // regime subtracts only the employer PF (handled inside calcTaxableIncome).
    // Meal allowance paid as a VOUCHER is tax-exempt (old regime, capped);
    // paid as CASH it stays fully taxable.
    const mealVoucherAnnual = (record.mealAllowanceType !== 'cash') ? meal * 12 : 0;
    const tds = formData.tdsApplicable && !isIntern
      ? calcTDS(grossAnnual, formData.taxRegime, declaration, pfEEAnnual, pfERAnnual, mealVoucherAnnual)
      : 0;

    // Employer PF reduces take-home only when it's included in CTC.
    const employerPfInNet = pfFromCtc ? pfER : 0;
    // Employee PF is deducted from take-home in both regimes. (Taxable income /
    // TDS still excludes employee PF in the new regime — see calcTaxableIncome.)
    // Mirrors the employee-side declaration page.
    const employeePfInNet = pfEE;
    const totDed = employeePfInNet + esicEE + pt + lwfEE + tds + employerPfInNet;
    const net = gross - totDed;
    const ctc = gross + (pfFromCtc ? 0 : pfER) + esicER + eps + edli + lwfER;

    return { gross, pfEE, pfER, eps, edli, esicEE, esicER, pt, lwfEE, lwfER, tds, totDed, net, ctc, customTotal };
  }, [pfApplicable, pfOptOut, pfBasis, pfEmployeeRate, pfFromCtc, esicApplicable, ptApplicable, ptState, lwfApplicable, lwfState, declaration, formData.tdsApplicable, formData.taxRegime, formData.role]);

  useEffect(() => {
    if (ctcEnabled && ctcValue > 0 && salaryRecords.length > 0) {
      if (skipCtcAutoSplitRef.current) {
        skipCtcAutoSplitRef.current = false;
        return;
      }
      const monthlyCTC = isYearly ? ctcValue / 12 : ctcValue;
      const c = splitCtcToComponents(monthlyCTC);

      updateSalaryRecord(salaryRecords[0].id, 'basicSalary', c.basic);
      updateSalaryRecord(salaryRecords[0].id, 'hra', c.hra);
      updateSalaryRecord(salaryRecords[0].id, 'conveyance', c.conveyance);
      updateSalaryRecord(salaryRecords[0].id, 'medicalallowance', c.medicalallowance);
      updateSalaryRecord(salaryRecords[0].id, 'mealAllowance', c.mealAllowance);
      updateSalaryRecord(salaryRecords[0].id, 'allowance', c.allowance);
      updateSalaryRecord(salaryRecords[0].id, 'lta', c.lta);
      updateSalaryRecord(salaryRecords[0].id, 'others', c.others);
      updateSalaryRecord(salaryRecords[0].id, 'bonusAmount', c.bonusAmount);
      // Keep the first component's own CTC in sync with the top-level box so
      // every component (incl. the first) carries an annual CTC of its own.
      updateSalaryRecord(salaryRecords[0].id, 'ctc', Math.round(isYearly ? ctcValue : ctcValue * 12));
    }
  }, [ctcEnabled, ctcValue, isYearly, salaryRecords.length, updateSalaryRecord]);

  // Per-component CTC entry (used by the 2nd+ revision cards). Updating a
  // record's CTC re-splits ONLY that record's components, leaving the others
  // untouched — this is what lets a mid-year raise be its own component.
  const updateRecordCtc = useCallback((id: string, annualCtc: number) => {
    const monthly = (Number(annualCtc) || 0) / 12;
    const c = splitCtcToComponents(monthly);
    setSalaryRecords(prev =>
      prev.map(r =>
        r.id === id
          ? {
              ...r,
              ctc: Math.round(Number(annualCtc) || 0),
              basicSalary: c.basic,
              hra: c.hra,
              conveyance: c.conveyance,
              medicalallowance: c.medicalallowance,
              mealAllowance: c.mealAllowance,
              allowance: c.allowance,
              lta: c.lta,
              others: c.others,
              bonusAmount: c.bonusAmount,
            }
          : r
      )
    );
  }, []);

  // Calculate CTC from current components
  const calculateCTCFromComponents = useCallback(() => {
    if (salaryRecords.length > 0) {
      const details = calculateSalaryDetailsForRecord(salaryRecords[0]);
      const annualCTC = details.ctc * 12;
      setCtcValue(Math.round(annualCTC));
      setIsYearly(true);
      toast({
        title: "CTC Calculated",
        description: `Annual CTC: ₹${fmt(annualCTC)}`,
      });
    }
  }, [salaryRecords, calculateSalaryDetailsForRecord]);

  // Format a Date as a plain ISO 'YYYY-MM-DD' (local, no timezone shift).
  const toISODate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // End date per component = the day BEFORE the next component's effective
  // date; null (open-ended) for the latest component. Keyed by record id so
  // the cards and the tax calc stay in agreement.
  const recordEndDates = useMemo<Record<string, Date | null>>(() => {
    const ordered = salaryRecords
      .filter((r) => r.effectiveDate)
      .map((r) => ({ r, t: new Date(r.effectiveDate).getTime() }))
      .sort((a, b) => a.t - b.t);
    const map: Record<string, Date | null> = {};
    ordered.forEach((cur, idx) => {
      const next = ordered[idx + 1];
      if (next) {
        const d = new Date(next.r.effectiveDate);
        d.setDate(d.getDate() - 1);
        map[cur.r.id] = d;
      } else {
        map[cur.r.id] = null;
      }
    });
    return map;
  }, [salaryRecords]);

  // FY-wide, prorated income tax. Blends every component for the months its
  // CTC was in force, adds any previous-employer income, taxes the total once,
  // credits previous-employer TDS and spreads the balance over the months
  // worked. This is the fix for "the first component's tax was repeated on the
  // next one" — there is now ONE annual figure, not a per-record full year.
  const fyTax = useMemo(() => {
    const { start: fyStart, end: fyEnd } = fyBounds(financialYear);
    const slices: FySlice[] = salaryRecords.map((r) => {
      const d = calculateSalaryDetailsForRecord(r);
      const effStart = r.effectiveDate ? new Date(r.effectiveDate) : null;
      const effEnd = recordEndDates[r.id] || null;
      return {
        monthlyGross: d.gross,
        months: monthsActiveInFy(effStart, effEnd, fyStart, fyEnd),
        pfEEMonthly: d.pfEE,
        pfERMonthly: d.pfER,
        mealVoucherMonthly: (r.mealAllowanceType !== 'cash') ? (Number(r.mealAllowance) || 0) : 0,
      };
    });
    const prior = {
      taxableIncome: Number(prevEmployerIncome) || 0,
      tdsPaid: Number(prevEmployerTds) || 0,
    };
    const base = calcFyTax(slices, formData.taxRegime, declaration, prior);
    // Respect the company/employee TDS-applicable flag: no flag → no TDS.
    if (!formData.tdsApplicable) {
      return { ...base, annualTax: 0, balanceTax: 0, monthlyTds: 0 };
    }
    return base;
  }, [
    salaryRecords,
    recordEndDates,
    financialYear,
    prevEmployerIncome,
    prevEmployerTds,
    formData.tdsApplicable,
    formData.taxRegime,
    declaration,
    calculateSalaryDetailsForRecord,
  ]);

  useEffect(() => {
    dispatch(getDesignationList());
  }, []);

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  useEffect(() => {
    dispatch(getManagerList(formData.department_id || "all"));
  }, [formData.department_id, formData.role]);

  useEffect(() => {
    if (formData.sameAs) {
      setFormData((prev) => ({
        ...prev,
        permanent_address1: prev.current_address1,
        permanent_address2: prev.current_address2,
        permanent_city: prev.current_city,
        permanent_state: prev.current_state,
        permanent_pin: prev.current_pin,
      }));
    }
  }, [formData.current_address1, formData.current_address2, formData.current_city, formData.current_state, formData.current_pin, formData.sameAs]);

  useEffect(() => {
    if (formData.workfromhome_list.length > 0) {
      setFormData((prev) => ({ ...prev, workfromhome_list: [] }));
    }
  }, [formData.modeOfWorking]);


  // Retainer-ship comes straight off the employee payload (GET /employee_by_ID
  // returns `retainer`), so it hydrates here and NOT inside the compliance
  // effect below: that one bails out when the branch has no compliance row
  // (`if (!settings) return`) or when the request fails, which used to leave
  // the card blank for an employee who does have a retainer — and the blank
  // card then cancelled it on the next save.
  useEffect(() => {
    const empRet = (employee as any)?.retainer;
    if (empRet) {
      setRetainerEnabled(!!empRet.enabled);
      setRetainerAmount(Number(empRet.amount) || 0);
      setRetainerPeriod(Number(empRet.period_months) || 3);
      setRetainerRecurring(!!empRet.recurring);
      setRetainerFloor(Number(empRet.min_take_home_floor) || 0);
      if (empRet.start_year && empRet.start_month)
        setRetainerStart(`${empRet.start_year}-${String(empRet.start_month).padStart(2, '0')}`);
    } else {
      // No config of their own — back to a blank card, so switching employees
      // can never carry the previous one's amount over. The org-wide defaults
      // are prefilled on top of this by the compliance effect below.
      setRetainerEnabled(false);
      setRetainerAmount(0);
      setRetainerPeriod(3);
      setRetainerRecurring(false);
      setRetainerFloor(0);
      setRetainerStart(new Date().toISOString().slice(0, 7));
    }
    setRetainerHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id]);

   useEffect(() => {
    const fetchComplianceSettings = async () => {
      try {
        const response = await getCompanyComplianceSettings();

        // Backend returns the raw array; also tolerate `{ data: ... }`.
        let settings: any = null;
        if (Array.isArray(response)) {
          settings = response[0];
        } else if (response?.data) {
          settings = Array.isArray(response.data) ? response.data[0] : response.data;
        } else {
          settings = response;
        }

        if (!settings) return; // no company record yet → keep initial defaults

        const truthy = (v: any) => v === true || v === 1 || v === "true";

        // Company values for the 5 per-employee flags.
        const companyPf = truthy(settings.pfApplicable);
        const companyEsic = truthy(settings.esicApplicable);
        const companyPt = truthy(settings.ptApplicable);
        const companyLwf = truthy(settings.lwfApplicable);
        const companyTds = truthy(settings.tdsApplicable);
        const tax = employee?.TaxConfig?.[0];
        let pfApp = companyPf;
        let esicApp = companyEsic;
        let ptApp = companyPt;
        let lwfApp = companyLwf;
        let tdsApp = companyTds;

        if (tax) {
          pfApp = truthy(tax.pfApplicable);
          esicApp = truthy(tax.esicApplicable);
          ptApp = truthy(tax.ptApplicable);
          lwfApp = truthy(tax.lwfApplicable);
          tdsApp = truthy(tax.tdsApplicable);
        }

        // Interns bypass every statutory component and income tax — force all
        // applicability flags OFF so the persisted TaxConfig matches the
        // zero-deduction stipend the calculation produces.
        const internRole = (employee?.role || formData.role || "").toString().trim().toLowerCase() === "intern";
        if (internRole) {
          pfApp = false; esicApp = false; ptApp = false; lwfApp = false; tdsApp = false;
        }

        setPfApplicable(pfApp);
        setEsicApplicable(esicApp);
        setPtApplicable(ptApp);
        setLwfApplicable(lwfApp);

        // Retainer: the employee's OWN config is hydrated by its own effect
        // above, so it survives this fetch failing or returning nothing. Only
        // the org-wide DEFAULTS need `settings`, and they must never overwrite
        // a config the employee already has.
        if (!(employee as any)?.retainer && truthy(settings.retainerEnabled)) {
          setRetainerAmount(Number(settings.retainerDefaultAmount) || 0);
          setRetainerPeriod(Number(settings.retainerDefaultPeriod) || 3);
          setRetainerRecurring(truthy(settings.retainerDefaultRecurring));
          setRetainerFloor(Number(settings.retainerDefaultFloor) || 0);
        }
        // EPF opt-out chosen by the employee on their declaration screen.
        setPfOptOut(truthy(tax ? (tax as any).pfOptOut : (employee as any)?.pfOptOut));
        // Declaration lock is now PER COMPONENT. This banner-level flag is true
        // when ANY component is locked (falls back to the legacy per-employee
        // TaxConfig flag for rows saved before the per-component column existed).
        const _anyCompLocked = (employee?.SalaryDetails || []).some(
          (d: any) => truthy(d.declaration_locked ?? d.declarationLocked),
        );
        const _declLocked =
          _anyCompLocked ||
          truthy(tax ? (tax as any).declarationLocked : (employee as any)?.declarationLocked);
        setDeclarationLocked(_declLocked);

        // When the employee already has their own saved tax config, that
        // per-employee record (regime, PF basis/rate, declarations) wins
        // over the company defaults — otherwise reopening the edit panel
        // would silently reset a saved "Old Regime" (or a custom PF rate /
        // declaration) back to the company's default. Fall back to company
        // settings only for fields never saved for this employee.
        const pickTax = (field: string, fallback: any) => {
          const v = tax ? (tax as any)[field] : undefined;
          return v === undefined || v === null || v === "" ? fallback : v;
        };

        setFormData(prev => ({
          ...prev,
          tdsApplicable: tdsApp,
          pfBasis: pickTax("pfBasis", settings.pfBasis || "statutory"),
          taxRegime: pickTax("taxRegime", settings.taxRegime || "new"),
        }));

        setPfBasis(pickTax("pfBasis", settings.pfBasis || "statutory"));
        setPtState(pickTax("ptState", settings.ptState || "karnataka"));
        setLwfState(pickTax("lwfState", settings.lwfState || "karnataka"));
        setPfFromCtc(truthy(settings.pfFromCtc));
        setPfEmployeeRate(
          Math.max(
            12,
            Number(pickTax("pfEmployeeRate", (employee as any)?.pfEmployeeRate || settings.pfEmployeeRate || 12)),
          ),
        );

        setDeclaration(prev => ({
          ...prev,
          sec80c: Number(pickTax("sec80c", settings.sec80c)) || 0,
          sec80d: Number(pickTax("sec80d", settings.sec80d)) || 0,
          sec80ccd1b: Number(pickTax("sec80ccd1b", settings.sec80ccd1b)) || 0,
          hraExempt: Number(pickTax("hraExempt", settings.hraExempt)) || 0,
          homeLoan: Number(pickTax("homeLoan", settings.homeLoan)) || 0,
          ltaExempt: Number(pickTax("ltaExempt", settings.ltaExempt)) || 0,
          sec80g: Number(pickTax("sec80g", settings.sec80g)) || 0,
          sec80e: Number(pickTax("sec80e", settings.sec80e)) || 0,
          sec80u: Number(pickTax("sec80u", settings.sec80u)) || 0,
          npsEmployer: Number(pickTax("npsEmployer", settings.npsEmployer)) || 0,
          ptPaid: Number(pickTax("ptPaid", settings.ptPaid)) || 0,
          otherDed: Number(pickTax("otherDed", settings.otherDed)) || 0,
        }));
      } catch (error) {
        console.error("Error fetching compliance settings:", error);
      }
    };

    fetchComplianceSettings();
  }, [employee?.id, employee?.TaxConfig?.length]);

  // Interns bypass all compliance + tax. If the admin selects the Intern role
  // (including switching to it mid-form, which the compliance effect above does
  // not react to), force every statutory flag OFF so the toggles, persisted
  // TaxConfig, and the zero-deduction calculation all stay in sync.
  useEffect(() => {
    if ((formData.role || "").toString().trim().toLowerCase() !== "intern") return;
    setPfApplicable(false);
    setEsicApplicable(false);
    setPtApplicable(false);
    setLwfApplicable(false);
    setFormData((prev) => (prev.tdsApplicable ? { ...prev, tdsApplicable: false } : prev));
  }, [formData.role]);

  const showReportingPerson = true;
  const managerList = useSelector((state: any) => state.employee.managerList);
  const departmentList = useSelector((state: any) => state.employee.departmentList);
  const designationList = useSelector((state: any) => state.employee.designationList);

  const selectedDepartment = departmentList.find((d: any) => d.id === formData.department_id);
  const isExecutiveDepartment =
    (selectedDepartment?.name || "").trim().toLowerCase() === "executive";
  const ROLES_HIDDEN_FOR_EXECUTIVE = ["employee", "supervisor", "manager"];
  const assignable = assignableRoles(roleOptions, currentUserRole);
  const visibleRoles = isExecutiveDepartment
    ? assignable.filter((r) => !ROLES_HIDDEN_FOR_EXECUTIVE.includes(r.role_key))
    : assignable;

  useEffect(() => {
    if (isExecutiveDepartment && ROLES_HIDDEN_FOR_EXECUTIVE.includes(formData.role)) {
      setFormData((prev) => ({ ...prev, role: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExecutiveDepartment]);

  const adminFromList = managerList.find((u: any) => u.id === "admin");
  const ADMIN_OPTION = adminFromList || { id: "admin", name: "Admin", role: "admin" };

  const executiveDepartmentIds = new Set(
    (departmentList || [])
      .filter((d: any) => (d?.name || "").trim().toLowerCase() === "executive")
      .map((d: any) => d.id)
  );

  const filteredManagerList = managerList
    .filter((user: any) => user.id !== "admin")
    .filter((user: any) => {
      const isExecutive =
        !!user.department_id && executiveDepartmentIds.has(user.department_id);

      // No department picked yet → restrict the default list to Admin
      // (added separately below) + Executive-department employees only.
      // Otherwise we'd surface managers / supervisors that the user
      // hasn't chosen a reporting line for yet.
      if (!formData.department_id) return isExecutive;

      // Executive employees are always eligible once a department is set.
      if (isExecutive) return true;

      // Department-scoped role hierarchy for everyone else.
      if (formData.role === "manager") return user.role === "hr head" || user.role === "hr admin";
      if (formData.role === "supervisor") return ["hr head", "hr admin", "manager"].includes(user.role);
      // Intern is the employee tier — same reporting-person hierarchy as employee.
      if (formData.role === "employee" || formData.role === "intern") return ["hr head", "hr admin", "manager", "supervisor"].includes(user.role);
      if (formData.role === "director") return ["hr head", "hr admin", "manager", "supervisor"].includes(user.role);
      if (formData.role === "founder") return ["hr head", "hr admin", "manager", "supervisor"].includes(user.role);
      return false;
    });

  const reportingOptions = [ADMIN_OPTION, ...filteredManagerList];

  // Auto-select Admin as the default reporting person when no manager is set yet.
  useEffect(() => {
    if (showReportingPerson && !formData.manager_id) {
      setFormData((prev) => ({ ...prev, manager_id: ADMIN_OPTION.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReportingPerson]);

  const handleAddDepartment = async () => {
    const name = (departmentName || "").trim();
    if (!name) {
      toast({
        title: "Name required",
        description: "Please enter a department name before saving.",
        variant: "destructive",
      });
      return;
    }
    // Client-side duplicate check (case-insensitive). Backend also rejects
    // duplicates with a 400 — caught below in case the list is stale.
    const existing = (departmentList || []).find(
      (d: any) => (d.name || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      toast({
        title: "Department already exists",
        description: `“${existing.name}” is already in the list — pick it from the dropdown instead.`,
        variant: "destructive",
      });
      return;
    }
    try {
      await api.post("/add_department", { name });
      setDepartmentName("");
      setAddDepartmentModal(false);
      dispatch(getDepartmentList());
      toast({
        title: "Department added",
        description: `“${name}” is now available in the dropdown.`,
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Could not add department";
      const isDuplicate =
        err?.response?.status === 400 &&
        typeof detail === "string" &&
        detail.toLowerCase().includes("already");
      toast({
        title: isDuplicate ? "Department already exists" : "Could not add department",
        description: detail,
        variant: "destructive",
      });
    }
  };

  const handleAddDesignation = async () => {
    const name = (designationName || "").trim();
    if (!name) {
      toast({
        title: "Name required",
        description: "Please enter a designation name before saving.",
        variant: "destructive",
      });
      return;
    }
    const existing = (designationList || []).find(
      (d: any) => (d.name || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      toast({
        title: "Designation already exists",
        description: `“${existing.name}” is already in the list — pick it from the dropdown instead.`,
        variant: "destructive",
      });
      return;
    }
    try {
      await api.post("/add_designation", { name });
      setDesignationName("");
      setAddDesignationModal(false);
      dispatch(getDesignationList());
      toast({
        title: "Designation added",
        description: `“${name}” is now available in the dropdown.`,
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Could not add designation";
      const isDuplicate =
        err?.response?.status === 400 &&
        typeof detail === "string" &&
        detail.toLowerCase().includes("already");
      toast({
        title: isDuplicate ? "Designation already exists" : "Could not add designation",
        description: detail,
        variant: "destructive",
      });
    }
  };

  const { toast } = useToast();

  const REQUIRED_PERSONAL_FIELDS = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'employeeID', label: 'Employee ID' },
    { key: 'email', label: 'Email Address' },
    { key: 'gender', label: 'Gender' },
    { key: 'phone', label: 'Phone Number' },
    { key: 'department_id', label: 'Department' },
    { key: 'role', label: 'Role' },
    { key: 'designation_id', label: 'Designation' },
    { key: 'hire_date', label: 'Date of Joining' },
    { key: 'country', label: 'Country' },
    { key: 'current_address1', label: 'Current Address Line 1' },
    { key: 'current_city', label: 'Current City' },
    { key: 'current_state', label: 'Current State' },
    { key: 'current_pin', label: 'Current Pincode' },
  ];

  const ALWAYS_REQUIRED_KEYS = new Set([
    'email',
    'employeeID',
    'hire_date',
    ...(employee?.id ? [] : ['password']),
  ]);

  const isReq = (key: string): boolean => {
    if (ALWAYS_REQUIRED_KEYS.has(key)) return true;
    if (isMandatoryDisabled) return false;
    return REQUIRED_PERSONAL_FIELDS.some((f) => f.key === key);
  };

  // Tiny red asterisk used after every required label.
  const Req = ({ on }: { on: boolean }) =>
    on ? <span className="text-red-500 ml-0.5">*</span> : null;

  const formRef = useRef<HTMLFormElement>(null);

  const getRequiredFields = () => {
    const fields = [...REQUIRED_PERSONAL_FIELDS];
    if (!employee?.id) {
      fields.push({ key: 'password', label: 'Password' });
    }
    return fields;
  };

  const validatePersonalDetails = (): string[] => {

    // ✅ ADD THIS LINE (FULL SKIP)
    if (isMandatoryDisabled) {
      return [];
    }

    const missingFields: string[] = [];
    const requiredFields = getRequiredFields();

    for (const field of requiredFields) {
      const value = formData[field.key as keyof typeof formData];

      if (!value || (typeof value === 'string' && value.trim() === '')) {
        missingFields.push(field.label);
      }
    }

    // optional validations (only if value exists)
    if (formData.phone && formData.phone.length !== 10) {
      missingFields.push('Phone Number (must be 10 digits)');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      missingFields.push('Valid Email Address');
    }

    if (formData.current_pin && !/^\d{6}$/.test(formData.current_pin)) {
      missingFields.push('Current Pincode (must be 6 digits)');
    }

    return missingFields;
  };

  const scrollToTopOfPage = () => {
    const doScroll = () => {
      try {
        let el: HTMLElement | null = formRef.current;
        while (el) {
          const style = window.getComputedStyle(el);
          const oy = style.overflowY;
          const scrollable =
            (oy === "auto" || oy === "scroll" || oy === "overlay") &&
            el.scrollHeight > el.clientHeight;
          if (scrollable) el.scrollTop = 0;
          el = el.parentElement;
        }
      } catch {}
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
      if (typeof document !== "undefined") {
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      }
    };

    doScroll();
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(doScroll);
    }
  };

  // Step order respects the access profile (Salary/Documents may be hidden).
  const { hasAccess: accHasAccess, permissions: accPermissions } = useAccess();
  const orderedSteps = allowedEmployeeSteps(accHasAccess, accPermissions);
  const _stepIdx = orderedSteps.indexOf(activeTab);
  const nextStepId =
    _stepIdx >= 0 && _stepIdx < orderedSteps.length - 1 ? orderedSteps[_stepIdx + 1] : null;
  const prevStepId = _stepIdx > 0 ? orderedSteps[_stepIdx - 1] : null;
  const nextStepLabel = nextStepId ? EMP_STEP_LABEL[nextStepId] : "";
  const prevStepLabel = prevStepId ? EMP_STEP_LABEL[prevStepId] : "";

  const handleNext = () => {
    if (activeTab === "personal") {
      const form = formRef.current;
      if (form && !form.checkValidity()) {
        form.reportValidity();
        return;
      }
    }
    if (nextStepId) setActiveTab(nextStepId);
    scrollToTopOfPage();
  };
  const handleBack = () => {
    if (prevStepId) setActiveTab(prevStepId);
    scrollToTopOfPage();
  };

 const buildTaxConfig = () => {
  // Fields the employee owns on their declaration screen (UAN / PF member ID,
  // PF-change counter, declaration lock). The admin form doesn't edit these,
  // so carry them through verbatim — the update path replaces the whole row,
  // and omitting them would wipe the employee's saved values.
  const tax0: any = (employee as any)?.TaxConfig?.[0] || {};
  return [
    {
      pfApplicable: pfApplicable,
      esicApplicable: esicApplicable,
      ptApplicable: ptApplicable,
      lwfApplicable: lwfApplicable,
      tdsApplicable: formData.tdsApplicable,
      // Per-employee tax policy — must be sent so the regime, PF basis/rate
      // and TDS declarations are saved against this employee (and not lost
      // back to the company defaults on the next edit).
      pfBasis: pfBasis,
      pfEmployeeRate: pfEmployeeRate,
      // Preserve the employee's EPF opt-out so an admin edit doesn't reset it.
      pfOptOut: pfOptOut,
      uan: tax0.uan ?? null,
      pfMemberId: tax0.pfMemberId ?? null,
      pfChangeCount: tax0.pfChangeCount ?? 0,
      // The admin can unlock a saved declaration here (declarationLocked=false),
      // which re-opens it for the employee to edit.
      declarationLocked: declarationLocked,
      ptState: ptState,
      lwfState: lwfState,
      taxRegime: formData.taxRegime,
      sec80c: declaration.sec80c,
      sec80d: declaration.sec80d,
      sec80ccd1b: declaration.sec80ccd1b,
      hraExempt: declaration.hraExempt,
      homeLoan: declaration.homeLoan,
      ltaExempt: declaration.ltaExempt,
      sec80g: declaration.sec80g,
      sec80e: declaration.sec80e,
      sec80u: declaration.sec80u,
      npsEmployer: declaration.npsEmployer,
      ptPaid: declaration.ptPaid,
      otherDed: declaration.otherDed,
    },
  ];
};

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const annualCtc = Math.round(isYearly ? ctcValue : ctcValue * 12);
      // One FY-wide monthly TDS for every component (the prorated annual tax,
      // net of any previous-employer TDS, spread over the months worked).
      const fyMonthlyTds = fyTax.monthlyTds;
      const salaryDetailsArray = salaryRecords.map((record, index) => {
        const details = calculateSalaryDetailsForRecord(record);
        const endDate = recordEndDates[record.id] || null;
        // Each component now stores its OWN annual CTC. The first one keeps the
        // admin's verbatim top-level figure; the rest use their entered CTC,
        // falling back to a reconstruction only if left blank.
        const recordCtc =
          index === 0
            ? annualCtc
            : Math.round(Number(record.ctc) || details.gross * 12);
        // Replace the old per-record full-year TDS with the blended FY figure
        // and adjust net pay to match (net already netted details.tds out).
        const netWithFyTds = Math.round(details.net + details.tds - fyMonthlyTds);
        return {
          basicsalary: record.basicSalary,
          hra: record.hra,
          conveyance: record.conveyance,
          medicalallowance: record.medicalallowance,
          mealallowance: record.mealAllowance,
          meal_allowance_type: record.mealAllowanceType || 'cash',
          allowance: record.allowance,
          lta: record.lta,
          others: record.others,
          bonus: 0, // bonus is a per-month Payroll Records item, not a salary component
          ctc: recordCtc,
          pf: details.pfEE,
          esi: details.esicEE,
          proftax: details.pt,
          labourwelfare: details.lwfEE,
          tds: fyMonthlyTds,
          netsalary: netWithFyTds,
          // New: effective/end dates, FY and previous-employer figures so the
          // prorated tax is reproducible server-side and on the next edit.
          effectiveDate: record.effectiveDate,
          effective_date: record.effectiveDate || null,
          end_date: endDate ? toISODate(endDate) : null,
          // Bucket by the component's OWN effective date so one effective
          // 01-Apr-2025 always lands in FY 2025-26, regardless of which FY
          // filter the admin had selected while saving.
          financial_year: record.effectiveDate
            ? currentFinancialYear(new Date(record.effectiveDate))
            : financialYear,
          prev_employer_income: prevEmployerIncome || 0,
          prev_employer_tds: prevEmployerTds || 0,
          // PF rate is PER COMPONENT — preserve each component's own rate/lock so
          // an admin save never overwrites one component's PF % with another's.
          // A brand-new component (no stored rate) falls back to the form's rate.
          pf_employee_rate: Math.max(12, Number(record.pfEmployeeRate) || pfEmployeeRate || 12),
          pf_rate_locked: record.pfRateLocked ?? pfRateLocked,
          // Effective per-component EPF flag (mandatory ON at/below ₹15,000
          // basic; otherwise this component's own choice). Establishment-level
          // pfApplicable still gates it.
          pf_applicable:
            pfApplicable &&
            (Number(record.basicSalary) <= 15000 ? true : record.pfApplicable !== false),
          // Per-component declaration lock. A component is locked either because
          // the employee declared it, or because a NEWER component superseded it
          // (it now has an end_date — the same rule the backend applies). Sending
          // the derived value keeps the stored flag in step with what this screen
          // already shows, instead of the lock only appearing after the *next*
          // save. The open/latest component has no end_date → stays editable so
          // the employee can declare it before it locks in turn.
          declaration_locked: !!record.declarationLocked || !!endDate,
          status: record.status,
          customComponents: record.customComponents
        };
      });

      // Other financial years' components are not edited on this screen — carry
      // them through verbatim so saving the selected year never drops another
      // year's salary setup. Each keeps its OWN stored financial_year/values.
      const preservedDetails = (employee?.SalaryDetails || [])
        .filter((d: any) => fyOfDetail(d) !== financialYear)
        .map((d: any) => {
          const eff = (d.effective_date || d.effectiveDate || d.date || '')?.toString().split('T')[0] || null;
          return {
            basicsalary: d.basicsalary || 0,
            hra: d.hra || 0,
            conveyance: d.conveyance || 0,
            medicalallowance: d.medicalallowance || 0,
            mealallowance: d.mealallowance || 0,
            meal_allowance_type: (d.meal_allowance_type ?? d.mealAllowanceType) === 'voucher' ? 'voucher' : 'cash',
            allowance: d.allowance || 0,
            lta: d.lta || 0,
            others: d.others || 0,
            bonus: d.bonus || 0,
            ctc: Number(d.ctc) || 0,
            pf: d.pf || 0,
            esi: d.esi || 0,
            proftax: d.proftax || 0,
            labourwelfare: d.labourwelfare || 0,
            tds: d.tds || 0,
            netsalary: d.netsalary || 0,
            effectiveDate: eff,
            effective_date: eff,
            end_date: d.end_date ? d.end_date.toString().split('T')[0] : null,
            financial_year: fyOfDetail(d),
            // Preserve the other-FY component's stored EPF flag verbatim.
            pf_applicable: (d.pf_applicable ?? d.pfApplicable) !== false,
            // Preserve the other-FY component's declaration lock verbatim.
            declaration_locked: !!(d.declaration_locked ?? d.declarationLocked),
            prev_employer_income: d.prev_employer_income || 0,
            prev_employer_tds: d.prev_employer_tds || 0,
            pf_employee_rate: Number(d.pf_employee_rate) || Number(d.pfEmployeeRate) || 12,
            pf_rate_locked: d.pf_rate_locked ?? d.pfRateLocked ?? false,
            status: d.status || 'active',
            customComponents: d.customComponents || [],
          };
        });

      // Every component except the newest one (across ALL financial years) is
      // superseded — a later component has taken over, so it is closed and its
      // declaration is locked. Applied to the whole set here because
      // recordEndDates only covers the year on screen, so the last component of
      // another year would otherwise stay flagged as open.
      const allSalaryDetails = [...preservedDetails, ...salaryDetailsArray];
      const newestEffective = allSalaryDetails.reduce(
        (max: number, d: any) =>
          d.effective_date ? Math.max(max, new Date(d.effective_date).getTime()) : max,
        -Infinity,
      );
      // Nothing carries an effective date (legacy rows only) → no component can
      // be shown to supersede another, so leave every stored flag alone.
      if (Number.isFinite(newestEffective)) {
        let newestSeen = false;
        // Walk newest → oldest so exactly ONE component stays open when several
        // share the newest effective date.
        [...allSalaryDetails]
          .sort((a: any, b: any) =>
            new Date(b.effective_date || 0).getTime() - new Date(a.effective_date || 0).getTime())
          .forEach((d: any) => {
            if (
              !newestSeen && d.effective_date &&
              new Date(d.effective_date).getTime() === newestEffective
            ) {
              newestSeen = true;
              return;
            }
            d.declaration_locked = true;
          });
      }

      const finalData = {
        ...formData,
        designation: getDesignationName(formData.designation_id),
        SalaryDetails: allSalaryDetails,
        TaxConfig: buildTaxConfig(),
        basicsalary: salaryRecords[0]?.basicSalary || 0,
        hra: salaryRecords[0]?.hra || 0,
        conveyance: salaryRecords[0]?.conveyance || 0,
        medicalallowance: salaryRecords[0]?.medicalallowance || 0,
        mealallowance: salaryRecords[0]?.mealAllowance || 0,
        allowance: salaryRecords[0]?.allowance || 0,
        lta: salaryRecords[0]?.lta || 0,
        others: salaryRecords[0]?.others || 0,
        bonus: 0, // bonus is a per-month Payroll Records item, not a salary component
        // Top-level net/tds now reflect the blended FY monthly TDS (same value
        // stored on the components) instead of the first record's full-year tax.
        netsalary: Math.round(
          calculateSalaryDetailsForRecord(salaryRecords[0]).net
          + calculateSalaryDetailsForRecord(salaryRecords[0]).tds
          - fyTax.monthlyTds
        ),
        salary_amount: calculateSalaryDetailsForRecord(salaryRecords[0]).gross,
        pfApplicable,
        esicApplicable,
        ptApplicable,
        lwfApplicable,
        pfBasis,
        pfEmployeeRate,
        pfOptOut,
        ptState,
        lwfState,
        // Left out entirely until the card has hydrated — add/update_employee
        // skip the retainer when the key is absent, so a form that never read
        // the stored config can never zero it out.
        ...(retainerHydrated
          ? {
              retainer: {
                enabled: retainerEnabled,
                amount: retainerAmount,
                period_months: retainerPeriod,
                recurring: retainerRecurring,
                min_take_home_floor: retainerFloor,
                start_year: retainerStart ? Number(retainerStart.split('-')[0]) : null,
                start_month: retainerStart ? Number(retainerStart.split('-')[1]) : null,
              },
            }
          : {}),
        tds: fyTax.monthlyTds,
        financial_year: financialYear,
        prev_employer_income: prevEmployerIncome || 0,
        prev_employer_tds: prevEmployerTds || 0,
        ...declaration,
      };

      const result: any = await onSubmit(finalData);

      const newId =
        result?.id ||
        result?.employee?.id ||
        result?.data?.id ||
        finalData.id;

      // Persist the Access Management assignment (separate mapping table).
      if (newId) {
        try {
          await assignEmployeeAccess(newId, formData.access_id || null);
        } catch (err) {
          console.warn("Failed to assign access profile", err);
        }
      }

      // Same shape for the shared attendance kiosk — its own membership table,
      // written only when the dropdown actually changed.
      if (newId && kioskId !== savedKioskId) {
        try {
          await assignEmployeeToKiosk(newId, kioskId || null);
          setSavedKioskId(kioskId);
        } catch (err) {
          console.warn("Failed to assign attendance kiosk", err);
        }
      }

      const stagedEntries = Object.entries(pendingDocs) as [EmployeeDocType, File][];
      if (newId && stagedEntries.length > 0) {
        for (const [docType, file] of stagedEntries) {
          try {
            await uploadEmployeeDocument(newId, docType, file);
          } catch (err: any) {
            toast({
              title: `Failed to upload ${docType.replace("_", " ")}`,
              description: err?.response?.data?.detail || err?.message || "Upload error",
              variant: "destructive",
            });
          }
        }
        setPendingDocs({});
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDepartmentName = (deptId: string) => {
    const dept = departmentList.find((d: any) => d.id === deptId);
    return dept?.name || deptId;
  };

  const getDesignationName = (designationId: string) => {
    const designation = designationList.find((d: any) => d.id === designationId);
    return designation?.name || designationId;
  };

  // The state mock labels are stored with a leading GST state code like
  // "17 Meghalaya". Strip the prefix when rendering / filtering so users see
  // just the state name and search matches against the name only.
  const cleanStateLabel = (label: string) => (label || "").replace(/^\d+\s*/, "");

  const getStateName = (stateId: string) => {
    const stateObj = state.find((s) => String(s.id) === stateId);
    return cleanStateLabel(stateObj?.label || stateId);
  };

  const getCountryName = (countryId: string) => {
    const countryObj = country.find((c) => String(c.id) === countryId);
    return countryObj?.label || countryId;
  };

  const getManagerName = (managerId: string) => {
    if (managerId === "admin") {
      const admin = managerList.find((m: any) => m.id === "admin");
      return admin?.name || "Admin";
    }
    const manager = managerList.find((m: any) => m.id === managerId);
    return manager?.name || managerId;
  };

  const isNewRegime = formData.taxRegime === "new";

  useEffect(() => {
    if (employee && designationList.length > 0) {
      const matchedDesignation = designationList.find(
        (d: any) => d.name === employee.designation
      );

      if (matchedDesignation) {
        setFormData((prev) => ({
          ...prev,
          designation_id: matchedDesignation.id,
        }));
      }
    }
  }, [employee, designationList]);

  const isAddMode = !employee;
  const tabsLocked = isAddMode;

  const TAB_ORDER: EmployeeStep[] = ["personal", "salary", "documents", "summary"];
  const tabIndex = (t: EmployeeStep) => TAB_ORDER.indexOf(t);
  const [maxReachedTab, setMaxReachedTab] = useState<EmployeeStep>(
    initialTab || "personal",
  );
  useEffect(() => {
    setMaxReachedTab((prev) =>
      tabIndex(activeTab) > tabIndex(prev) ? activeTab : prev,
    );
  }, [activeTab]);

  const personalMissingFields = isAddMode ? validatePersonalDetails() : [];
  const personalIsValid = personalMissingFields.length === 0;

  const unlockedTabs: EmployeeStep[] = (() => {
    if (!isAddMode) return TAB_ORDER;
    if (personalIsValid) return TAB_ORDER;
    const reachedIdx = tabIndex(maxReachedTab);
    return TAB_ORDER.slice(0, reachedIdx + 1);
  })();

  const unlockedTabsKey = unlockedTabs.join(",");
  useEffect(() => {
    onUnlockedTabsChange?.(unlockedTabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockedTabsKey, onUnlockedTabsChange]);

  // ─── Inline email / phone validation helpers ──────────────────────
  const normalize = (s: string | undefined | null) =>
    (s || "").trim().toLowerCase();
  // Same regex used by validatePersonalDetails, kept in sync so the
  // submit-time check and the inline indicator never disagree.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailFormatInvalid =
    !!formData.email && !EMAIL_RE.test(formData.email.trim());
  const isEmailDuplicate =
    !!formData.email &&
    !isEmailFormatInvalid &&
    (existingEmails || []).some(
      (e) => normalize(e) === normalize(formData.email),
    );
  const isPhoneDuplicate =
    !!formData.phone &&
    (existingPhones || []).some(
      (p) => (p || "").replace(/\D/g, "") === (formData.phone || "").replace(/\D/g, ""),
    );

  return (
    <form
      ref={formRef}
      id="employee-form"
      onSubmit={handleFinalSubmit}
      className="space-y-6"
    >
      {!hideTabNav && (
        <EmployeeStepsTabNav
          activeTab={activeTab}
          tabsLocked={tabsLocked}
          unlockedTabs={isAddMode ? unlockedTabs : undefined}
          onTabChange={(next) => {
            setActiveTab(next);
            scrollToTopOfPage();
          }}
        />
      )}

      {/* Personal Details Tab */}
      {activeTab === "personal" && (
        <div className="space-y-6">
          {/* Personal Information Card */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Personal Information
              </CardTitle>
              <CardDescription>Basic personal and contact details</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    First Name<Req on={isReq("first_name")} />
                  </Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        first_name: capitalizeFirstLetter(e.target.value),
                      })
                    }
                    required={isReq("first_name")}
                    className="mt-1.5 font-medium"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Middle Name</Label>
                  <Input
                    value={formData.middle_name}
                    onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                    className="mt-1.5"
                    placeholder="Enter middle name"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Last Name<Req on={isReq("last_name")} />
                  </Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        last_name: capitalizeFirstLetter(e.target.value),
                      })
                    }
                    required={isReq("last_name")}
                    className="mt-1.5 font-medium"
                    placeholder="Enter last name"
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Gender<Req on={isReq("gender")} />
                  </Label>

                  <Select
                    value={formData.gender || ""}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        gender: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Gender" />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="others">Others</SelectItem>

                    </SelectContent>
                  </Select>
                  {/* Hidden field so HTML5 validation also catches Gender if required */}
                  {isReq("gender") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.gender || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Employee ID<Req on={isReq("employeeID")} />
                  </Label>
                  <Input
                    value={formData.employeeID}
                    onChange={(e) => setFormData({ ...formData, employeeID: e.target.value })}
                    required={isReq("employeeID")}
                    className="mt-1.5 font-mono"
                    placeholder="EMP001"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Mail className="h-3 w-3 inline mr-1" />
                    Email Address<Req on={isReq("email")} />
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required={isReq("email")}
                    aria-invalid={isEmailFormatInvalid || isEmailDuplicate || undefined}
                    className={`mt-1.5 ${
                      isEmailFormatInvalid || isEmailDuplicate
                        ? "border-red-400 focus:ring-red-500 focus:border-red-500"
                        : ""
                    }`}
                    placeholder="employee@company.com"
                  />
                  {/* Format error takes precedence — when the address
                      itself is malformed, the duplicate check is moot. */}
                  {isEmailFormatInvalid && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      Enter a valid email address (e.g. name@company.com)
                    </p>
                  )}
                  {isEmailDuplicate && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      This email is already added for another employee
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Phone className="h-3 w-3 inline mr-1" />
                    Phone Number<Req on={isReq("phone")} />
                  </Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    minLength={10}
                    pattern="\d{10}"
                    title="Phone number must be exactly 10 digits"
                    placeholder="Enter 10 digit phone number"
                    value={formData.phone}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 10) {
                        setFormData({ ...formData, phone: value });
                      }
                    }}
                    onInvalid={(e) => {
                      // Keep the native tooltip text consistent with our inline message.
                      (e.currentTarget as HTMLInputElement).setCustomValidity(
                        formData.phone
                          ? 'Phone number must be exactly 10 digits'
                          : 'Phone number is required',
                      );
                    }}
                    onInput={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity('')}
                    required={isReq("phone")}
                    aria-invalid={
                      (!!formData.phone && formData.phone.length !== 10) || isPhoneDuplicate
                    }
                    className={`mt-1.5 ${
                      (formData.phone && formData.phone.length !== 10) || isPhoneDuplicate
                        ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                        : ''
                    }`}
                  />
                  {formData.phone && formData.phone.length !== 10 && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      Phone number must be exactly 10 digits ({formData.phone.length}/10)
                    </p>
                  )}
                  {formData.phone && formData.phone.length === 10 && isPhoneDuplicate && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      This phone number is already added for another employee
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Building2 className="h-3 w-3 inline mr-1" />
                    Department<Req on={isReq("department_id")} />
                  </Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <Select
                        value={formData.department_id}
                        onValueChange={(value) => setFormData({ ...formData, department_id: value })}
                        onOpenChange={(open) => { if (!open) setDeptSearch(""); }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {/* Search bar — pinned at the top, stop key events so Radix
                              Select doesn't intercept typing as a typeahead. */}
                          <div
                            className="sticky top-0 z-10 bg-white p-2 border-b"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="relative">
                              <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                autoFocus
                                placeholder="Search department…"
                                value={deptSearch}
                                onChange={(e) => setDeptSearch(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                              />
                            </div>
                          </div>
                          {/* List capped at ~5 rows (≈ 200px) before scroll */}
                          <div className="max-h-[200px] overflow-y-auto">
                            {(() => {
                              const q = deptSearch.trim().toLowerCase();
                              const filtered = q
                                ? departmentList.filter((d: any) => (d.name || "").toLowerCase().includes(q))
                                : departmentList;
                              if (filtered.length === 0) {
                                return (
                                  <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
                                );
                              }
                              return filtered.map((dept: any) => (
                                <SelectItem key={dept.id} value={dept.id}>
                                  {dept.name}
                                </SelectItem>
                              ));
                            })()}
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0"
                      onClick={() => setAddDepartmentModal(true)}
                      aria-label="Add Department"
                      title="Add Department"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {isReq("department_id") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.department_id || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <User className="h-3 w-3 inline mr-1" />
                    Role<Req on={isReq("role")} />
                  </Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => {
                      if (value === "__add_role__") { setShowNewRole(true); return; }
                      const picked = visibleRoles.find((r) => r.role_key === value);
                      setFormData({
                        ...formData,
                        role: value,
                        access_id: picked?.default_access_id || formData.access_id || "",
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {visibleRoles.map((role) => (
                        <SelectItem key={role.role_key} value={role.role_key}>
                          <span className="capitalize">{role.name}</span>
                        </SelectItem>
                      ))}
                      <SelectItem value="__add_role__" className="text-emerald-600 font-semibold">
                        + Add new role
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {isReq("role") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.role || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <FileText className="h-3 w-3 inline mr-1" />
                    Access
                  </Label>
                  <div className="mt-1.5">
                    <Select
                      value={formData.access_id || "none"}
                      onValueChange={(value) => {
                        if (value === "__add_access__") { setShowNewAccess(true); return; }
                        setFormData({ ...formData, access_id: value === "none" ? "" : value });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select access" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="none">No access (role default)</SelectItem>
                        {accessList.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="__add_access__" className="text-emerald-600 font-semibold">
                          + Add new access
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Controls which panels &amp; pages this user sees after login.
                  </p>
                </div>
                {/* Only rendered when the org actually has kiosks and the
                    editor is allowed to see them — an empty dropdown would be
                    a question nobody can answer. */}
                {kioskOptions.length > 0 && (
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <FileText className="h-3 w-3 inline mr-1" />
                      Attendance Kiosk
                    </Label>
                    <div className="mt-1.5">
                      <Select
                        value={kioskId || "none"}
                        onValueChange={(value) => setKioskId(value === "none" ? "" : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select kiosk" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="none">Not on a kiosk</SelectItem>
                          {kioskOptions.map((k) => (
                            <SelectItem key={k.id} value={k.id}>
                              {k.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Lets this employee mark attendance on that shared device. One
                      kiosk per employee.
                    </p>
                  </div>
                )}
                <RoleAccessQuickCreate
                  mode="role"
                  open={showNewRole}
                  onClose={() => setShowNewRole(false)}
                  onCreated={(r) => {
                    setRoleOptions((prev) =>
                      prev.some((x) => x.role_key === r.role_key) ? prev : [...prev, r]
                    );
                    // The role's linked default profile is new — refresh the
                    // access list so it shows in the Access dropdown.
                    listAccess().then(setAccessList).catch(() => {});
                    // Defer so the new option is registered and the dialog-close
                    // settle can't clobber the programmatic selection.
                    setTimeout(() => {
                      setFormData((fd) => ({
                        ...fd,
                        role: r.role_key,
                        access_id: r.default_access_id || fd.access_id || "",
                      }));
                    }, 0);
                  }}
                />
                <RoleAccessQuickCreate
                  mode="access"
                  open={showNewAccess}
                  onClose={() => setShowNewAccess(false)}
                  onCreated={(a) => {
                    setAccessList((prev) => (prev.some((x) => x.id === a.id) ? prev : [...prev, a]));
                    // Defer so the new option is registered and the dialog-close
                    // settle can't clobber the programmatic selection.
                    setTimeout(() => {
                      setFormData((fd) => ({ ...fd, access_id: a.id }));
                    }, 0);
                  }}
                />
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <FileText className="h-3 w-3 inline mr-1" />
                    Designation<Req on={isReq("designation_id")} />
                  </Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <Select
                        value={formData.designation_id}
                        onValueChange={(value) =>
                          setFormData({ ...formData, designation_id: value })
                        }
                        onOpenChange={(open) => { if (!open) setDesigSearch(""); }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Designation" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {/* Search bar pinned at top */}
                          <div
                            className="sticky top-0 z-10 bg-white p-2 border-b"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="relative">
                              <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                autoFocus
                                placeholder="Search designation…"
                                value={desigSearch}
                                onChange={(e) => setDesigSearch(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                              />
                            </div>
                          </div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {(() => {
                              const q = desigSearch.trim().toLowerCase();
                              const filtered = q
                                ? designationList.filter((d: any) => (d.name || "").toLowerCase().includes(q))
                                : designationList;
                              if (filtered.length === 0) {
                                return (
                                  <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
                                );
                              }
                              return filtered.map((d: any) => (
                                <SelectItem key={d.id} value={d.id}>
                                  {d.name}
                                </SelectItem>
                              ));
                            })()}
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0"
                      onClick={() => setAddDesignationModal(true)}
                      aria-label="Add Designation"
                      title="Add Designation"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {isReq("designation_id") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.designation_id || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Date of Joining<Req on={isReq("hire_date")} />
                  </Label>
                  <Input
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                    required={isReq("hire_date")}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Date of Birth
                  </Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth || ""}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Building2 className="h-3 w-3 inline mr-1" />
                    Country<Req on={isReq("country")} />
                  </Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => setFormData({ ...formData, country: value })}
                    onOpenChange={(open) => { if (!open) setCountrySearch(""); }}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select Country" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <div
                        className="sticky top-0 z-10 bg-white p-2 border-b"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="relative">
                          <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            autoFocus
                            placeholder="Search country…"
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                        </div>
                      </div>
                      {/* List capped at ~5 rows (≈ 200px) before scroll */}
                      <div className="max-h-[200px] overflow-y-auto">
                        {(() => {
                          const q = countrySearch.trim().toLowerCase();
                          const filtered = q
                            ? country.filter((e: any) => String(e.label || "").toLowerCase().includes(q))
                            : country;
                          if (filtered.length === 0) {
                            return <div className="px-3 py-2 text-xs text-gray-500">No matches</div>;
                          }
                          return filtered.map((e: any) => (
                            <SelectItem key={e.id} value={String(e.id)}>
                              {e.label}
                            </SelectItem>
                          ));
                        })()}
                      </div>
                    </SelectContent>
                  </Select>
                  {isReq("country") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.country || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Phone className="h-3 w-3 inline mr-1" />
                    Emergency Contact
                  </Label>
                  <Input
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.emergency_contacts}
                    onChange={(e) => setFormData({ ...formData, emergency_contacts: e.target.value })}
                    className="mt-1.5"
                    placeholder="Emergency contact number"
                  />
                </div>
                <div className="relative">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Password<Req on={isReq("password")} />
                  </Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => {
                      setPasswordTouched(true);
                      setFormData({ ...formData, password: e.target.value });
                    }}
                    required={isReq("password")}
                    className="mt-1.5 pr-10"
                    placeholder={
                      employee?.id ? "Leave blank to keep same password" : "••••••••"
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[38px] text-gray-500"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {showReportingPerson && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <User className="h-3 w-3 inline mr-1" />
                    Reporting Person
                  </Label>
                  <Select
                    value={formData.manager_id}
                    onValueChange={(value) => setFormData({ ...formData, manager_id: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select Reporting Person" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {reportingOptions.map((e: any) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} <span className="text-xs text-gray-500">({e.role.toUpperCase()})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Address Card */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                Current Address
              </CardTitle>
              <CardDescription>Residential address details</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="lg:col-span-2">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Address Line 1<Req on={isReq("current_address1")} />
                  </Label>
                  <Input
                    value={formData.current_address1}
                    onChange={(e) => setFormData({ ...formData, current_address1: e.target.value })}
                    required={isReq("current_address1")}
                    className="mt-1.5"
                    placeholder="House number, building name"
                  />
                </div>
                <div className="lg:col-span-2">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 2</Label>
                  <Input
                    value={formData.current_address2}
                    onChange={(e) => setFormData({ ...formData, current_address2: e.target.value })}
                    className="mt-1.5"
                    placeholder="Street, area, landmark"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    City<Req on={isReq("current_city")} />
                  </Label>
                  <Input
                    value={formData.current_city}
                    onChange={(e) => setFormData({ ...formData, current_city: e.target.value })}
                    required={isReq("current_city")}
                    className="mt-1.5"
                    placeholder="Enter city"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    State<Req on={isReq("current_state")} />
                  </Label>
                  <Select
                    value={formData.current_state}
                    onValueChange={(value) => setFormData({ ...formData, current_state: value })}
                    onOpenChange={(open) => { if (!open) setCurrStateSearch(""); }}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <div
                        className="sticky top-0 z-10 bg-white p-2 border-b"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="relative">
                          <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            autoFocus
                            placeholder="Search state…"
                            value={currStateSearch}
                            onChange={(e) => setCurrStateSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                        </div>
                      </div>
                      <div className="max-h-[200px] overflow-y-auto">
                        {(() => {
                          const q = currStateSearch.trim().toLowerCase();
                          const filtered = q
                            ? state.filter((e: any) => cleanStateLabel(e.label).toLowerCase().includes(q))
                            : state;
                          if (filtered.length === 0) {
                            return <div className="px-3 py-2 text-xs text-gray-500">No matches</div>;
                          }
                          return filtered.map((e: any) => (
                            <SelectItem key={e.id} value={String(e.id)}>
                              {cleanStateLabel(e.label)}
                            </SelectItem>
                          ));
                        })()}
                      </div>
                    </SelectContent>
                  </Select>
                  {isReq("current_state") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.current_state || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Pincode<Req on={isReq("current_pin")} />
                  </Label>
                  <Input
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.current_pin}
                    onChange={(e) => setFormData({ ...formData, current_pin: e.target.value })}
                    required={isReq("current_pin")}
                    className="mt-1.5"
                    placeholder="6 digit code"
                    pattern="\d{6}"
                    title="6 digit code"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Mode of Working</Label>
                  <Select
                    value={formData.modeOfWorking}
                    onValueChange={async (value) => {
                      // Gate "in-office" on a configured geofence — the
                      // attendance flow needs office lat / lon / radius
                      // to decide whether the employee is on premises.
                      // Remote / hybrid don't need this.
                      if (value === "in-office") {
                        const ok = await officeHasGeofence();
                        if (!ok) {
                          setOfficeLocationMissingOpen(true);
                          return; // don't apply the change
                        }
                      }
                      setFormData({
                        ...formData,
                        modeOfWorking: value,
                        workfromhome: value === "remote" || value === "hybrid",
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="in-office">🏢 In Office</SelectItem>
                      <SelectItem value="remote">🏠 Remote</SelectItem>
                      <SelectItem value="hybrid">🔄 Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Permanent Address Card */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  <CardTitle>Permanent Address</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sameAs"
                    checked={formData.sameAs}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({
                          ...formData,
                          sameAs: true,
                          permanent_address1: formData.current_address1,
                          permanent_address2: formData.current_address2,
                          permanent_city: formData.current_city,
                          permanent_state: formData.current_state,
                          permanent_pin: formData.current_pin,
                        });
                      } else {
                        setFormData({
                          ...formData,
                          sameAs: false,
                          permanent_address1: "",
                          permanent_address2: "",
                          permanent_city: "",
                          permanent_state: "",
                          permanent_pin: "",
                        });
                      }
                    }}
                  />
                  <Label htmlFor="sameAs" className="text-sm cursor-pointer">
                    Same as current address
                  </Label>
                </div>
              </div>
              <CardDescription>Permanent or correspondence address</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {formData.sameAs === false && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  <div className="lg:col-span-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 1</Label>
                    <Input
                      value={formData.permanent_address1}
                      onChange={(e) => setFormData({ ...formData, permanent_address1: e.target.value })}
                      className="mt-1.5"
                      placeholder="House number, building name"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 2</Label>
                    <Input
                      value={formData.permanent_address2}
                      onChange={(e) => setFormData({ ...formData, permanent_address2: e.target.value })}
                      className="mt-1.5"
                      placeholder="Street, area, landmark"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">City</Label>
                    <Input
                      value={formData.permanent_city}
                      onChange={(e) => setFormData({ ...formData, permanent_city: e.target.value })}
                      className="mt-1.5"
                      placeholder="Enter city"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">State</Label>
                    <Select
                      value={formData.permanent_state}
                      onValueChange={(value) => setFormData({ ...formData, permanent_state: value })}
                      onOpenChange={(open) => { if (!open) setPermStateSearch(""); }}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <div
                          className="sticky top-0 z-10 bg-white p-2 border-b"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="relative">
                            <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              autoFocus
                              placeholder="Search state…"
                              value={permStateSearch}
                              onChange={(e) => setPermStateSearch(e.target.value)}
                              onKeyDown={(e) => e.stopPropagation()}
                              className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto">
                          {(() => {
                            const q = permStateSearch.trim().toLowerCase();
                            const filtered = q
                              ? state.filter((e: any) => cleanStateLabel(e.label).toLowerCase().includes(q))
                              : state;
                            if (filtered.length === 0) {
                              return <div className="px-3 py-2 text-xs text-gray-500">No matches</div>;
                            }
                            return filtered.map((e: any) => (
                              <SelectItem key={e.id} value={String(e.id)}>
                                {cleanStateLabel(e.label)}
                              </SelectItem>
                            ));
                          })()}
                        </div>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Pincode</Label>
                    <Input
                      value={formData.permanent_pin}
                      onChange={(e) => setFormData({ ...formData, permanent_pin: e.target.value })}
                      className="mt-1.5"
                      placeholder="6 digit code"
                    />
                  </div>
                </div>
              )}
              {formData.sameAs && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <MapPin className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>Permanent address is same as current address</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bank Details Card — the 8 bank fields already live on formData
              and are forwarded on submit, so what's entered here shows on the
              employee's own "Payroll & Tax Summary" page and vice-versa. */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <Landmark className="h-5 w-5 text-blue-600" />
                Bank Details
              </CardTitle>
              <CardDescription>Account used to credit the employee's salary</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {BANK_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {f.label}
                    </Label>
                    {f.options ? (
                      <select
                        value={(formData as any)[f.key] || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, [f.key]: e.target.value })
                        }
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm mt-1.5"
                      >
                        <option value="">{f.placeholder}</option>
                        {f.options.map((opt) => (
                          <option key={opt} value={opt}>{opt.charAt(0) + opt.slice(1).toLowerCase()}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        value={(formData as any)[f.key] || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            [f.key]: f.uppercase
                              ? e.target.value.toUpperCase()
                              : e.target.value,
                          })
                        }
                        className="mt-1.5"
                        placeholder={f.placeholder}
                      />
                    )}
                  </div>
                ))}
                {/* Statutory identifiers — printed in the salary slip header
                    alongside the bank details, so they're collected here. */}
                {STATUTORY_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {f.label}
                    </Label>
                    <Input
                      value={(formData as any)[f.key] || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [f.key]: f.uppercase
                            ? e.target.value.toUpperCase()
                            : e.target.value,
                        })
                      }
                      className="mt-1.5"
                      placeholder={f.placeholder}
                    />
                  </div>
                ))}
                <div className="md:col-span-2 lg:col-span-4">
                  <BankAttachmentsField
                    value={(formData as any).bankAttachments || []}
                    onChange={(next) =>
                      setFormData({ ...formData, bankAttachments: next })
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Remote / hybrid employees mark attendance from anywhere — no
              label, schedule, location, or geofence is collected on the
              form. workfromhome_list stays empty; the backend schema
              already accepts an empty list, so the existing payload shape
              and downstream consumers continue to work. */}

          <div className="flex justify-end pt-4">
            <Button
              type="button"
              onClick={handleNext}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              Next: {nextStepLabel}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Salary & Deductions Tab with Multiple Records */}
      {activeTab === "salary" && (
        <div className="space-y-3">
          {/* Compact salary setup — only CTC, effective date, and tax
              regime are required at the time of add/update. Components
              are auto-calculated from CTC; the "View / Edit Salary
              Components" button below reveals the per-record detail
              cards for admins who need to tweak the computed split. */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-5 rounded-lg border border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  CTC <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number"
                    placeholder="Enter CTC"
                    value={ctcValue || ''}
                    onChange={(e) => {
                      // User-driven CTC edit → allow the auto-split to run.
                      skipCtcAutoSplitRef.current = false;
                      setCtcValue(Number(e.target.value));
                    }}
                    className="flex-1"
                  />
                  <Select
                    value={isYearly ? "yearly" : "monthly"}
                    onValueChange={(val) => {
                      skipCtcAutoSplitRef.current = false;
                      setIsYearly(val === "yearly");
                    }}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yearly">Yearly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {salaryRecords[0] && ctcValue > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Monthly CTC: ₹{fmt(calculateSalaryDetailsForRecord(salaryRecords[0]).gross)} ·
                    Net pay: ₹{fmt(Math.round(
                      calculateSalaryDetailsForRecord(salaryRecords[0]).net
                      + calculateSalaryDetailsForRecord(salaryRecords[0]).tds
                      - fyTax.monthlyTds
                    ))}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Effective Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="date"
                  value={salaryRecords[0]?.effectiveDate || ''}
                  onChange={(e) =>
                    salaryRecords[0] && updateSalaryRecord(salaryRecords[0].id, 'effectiveDate', e.target.value)
                  }
                  className="mt-1"
                />
                {salaryRecords[0] && recordEndDates[salaryRecords[0].id] && (
                  <p className="text-xs text-gray-500 mt-1">
                    Ends: {toISODate(recordEndDates[salaryRecords[0].id] as Date)} (next revision starts after)
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Tax Regime <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.taxRegime}
                  onValueChange={(value) => setFormData({ ...formData, taxRegime: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Regime" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New Regime (Default)</SelectItem>
                    <SelectItem value="old">Old Regime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowSalaryDetails(prev => !prev)}
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
              >
                {showSalaryDetails ? 'Hide' : 'View / Edit'} Salary Components
                <span className="ml-1">{showSalaryDetails ? '▲' : '▼'}</span>
              </Button>
            </div>
          </div>

          {/* Detailed per-record component cards — only rendered when
              the admin opens "View / Edit Salary Components". The
              auto-calculated split still drives the saved payload even
              when this section is collapsed. */}
          {showSalaryDetails && salaryRecords.map((record, index) => {
            const salaryDetails = calculateSalaryDetailsForRecord(record);
            const isCurrentRecord = index === 0;
            // Older components (a newer one exists on top) are read-only and
            // collapsed by default; only the most recent one is editable/open.
            const isOlder = index > 0;
            const isExpanded = !isOlder || expandedRecordIds.has(record.id);
            // Locked as soon as a newer component supersedes this one — derived
            // here rather than read from the stored flag, which the backend only
            // sets on save (that lag is why the previous component used to look
            // open until the NEXT component was added).
            const isSuperseded = isOlder || !!recordEndDates[record.id];

            return (
              <Card key={record.id} className={`border-0 shadow-md relative ${!isCurrentRecord ? 'opacity-95' : ''}`}>
                {/* Header (Salary Component N, date, status, delete) — only for
                    older components; the most recent one has no header. */}
                {index > 0 && (
                  <div className="px-6 py-3 rounded-t-xl flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                    <div className="flex items-center gap-4 flex-wrap">
                      <button
                        type="button"
                        onClick={() => toggleExpandedRecord(record.id)}
                        className="flex items-center gap-2"
                        title={isExpanded ? "Collapse" : "Expand"}
                      >
                        <span className="text-gray-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                        <div className={`w-3 h-3 rounded-full ${record.status === 'active' ? 'bg-green-500' : record.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-500'}`}></div>
                        <span className="text-sm font-semibold text-gray-700">
                          Salary Component {index + 1}
                        </span>
                      </button>
                      {(record.declarationLocked || isSuperseded) && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2.5 py-0.5">
                          🔒 {record.declarationLocked ? 'Locked by employee' : 'Locked (superseded)'}
                        </span>
                      )}
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-gray-500">Effective Date:</Label>
                        <Input
                          type="date"
                          value={record.effectiveDate}
                          disabled
                          className="h-8 w-40 text-sm bg-gray-100"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-gray-500">End Date:</Label>
                        <span className="text-sm font-medium text-gray-600">
                          {recordEndDates[record.id] ? toISODate(recordEndDates[record.id] as Date) : '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-gray-500">CTC (annual):</Label>
                        <Input
                          type="number"
                          value={record.ctc || ''}
                          disabled
                          className="h-8 w-32 text-sm bg-gray-100"
                          placeholder="Annual CTC"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSalaryRecord(record.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {isExpanded && (
                <fieldset disabled={isOlder} className="contents">
                <CardContent className={index === 0 ? "px-6 pb-6 pt-2" : "p-6"}>
                  {/* Add Custom Component Button for each record */}
                  <div className="flex justify-end mb-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const updatedRecords = [...salaryRecords];
                        const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                        updatedRecords[recordIndex].customComponents = [
                          ...(updatedRecords[recordIndex].customComponents || []),
                          { id: Date.now().toString(), name: "", amount: 0 }
                        ];
                        setSalaryRecords(updatedRecords);
                      }}
                      className="border-green-300 text-green-600 hover:bg-green-50"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Component
                    </Button>
                  </div>

                  {/* Standard Components Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Basic Salary *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.basicSalary || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'basicSalary', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">HRA (House Rent Allowance)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.hra || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'hra', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Conveyance Allowance</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.conveyance || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'conveyance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Medical Allowance</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.medicalallowance || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'medicalallowance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Meal Allowance / Food Coupons</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.mealAllowance || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'mealAllowance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                      {/* How the meal allowance is paid: food voucher (coupons) or cash. */}
                      <div className="mt-1.5 flex items-center gap-4">
                        {(['voucher', 'cash'] as const).map((opt) => (
                          <label key={opt} className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(record.mealAllowanceType || 'cash') === opt}
                              onChange={() => updateSalaryRecord(record.id, 'mealAllowanceType', opt)}
                              className="h-3.5 w-3.5 rounded text-emerald-600"
                            />
                            {opt === 'voucher' ? 'Voucher' : 'Cash'}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Special Allowance</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.allowance || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'allowance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">LTA (Leave Travel Allowance)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.lta || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'lta', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Other Allowances</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.others || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'others', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Per-component EPF toggle. EPF is MANDATORY (forced on,
                      checkbox disabled) when Basic ≤ ₹15,000; above the ceiling
                      it can be switched off so no EPF is deducted for the months
                      this component is in force. Hidden entirely for interns —
                      they have no EPF (and no other compliance) at all. */}
                  {(formData.role || "").toString().trim().toLowerCase() !== "intern" && (
                  <div className="mt-4 flex items-center flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded text-blue-600"
                      checked={Number(record.basicSalary) <= 15000 ? true : record.pfApplicable !== false}
                      disabled={Number(record.basicSalary) <= 15000}
                      onChange={(e) => updateSalaryRecord(record.id, 'pfApplicable', e.target.checked)}
                    />
                    <label className="text-sm font-medium text-gray-700">EPF applies to this component</label>
                    {Number(record.basicSalary) <= 15000 ? (
                      <span className="ml-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold px-2 py-0.5">
                        Mandatory · Basic ≤ ₹15,000
                      </span>
                    ) : (
                      record.pfApplicable === false && (
                        <span className="ml-1 rounded-full bg-red-100 text-red-700 text-[11px] font-semibold px-2 py-0.5">
                          EPF off · no PF deducted
                        </span>
                      )
                    )}
                  </div>
                  )}

                  {/* Custom Components Section for this record */}
                  {(record.customComponents && record.customComponents.length > 0) && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <Plus className="h-4 w-4 text-green-600" />
                          Custom Components
                        </Label>
                        <span className="text-xs text-gray-500">Click on component name to edit</span>
                      </div>
                      <div className="space-y-3">
                        {record.customComponents.map((component: any, compIndex: number) => (
                          <div key={component.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs font-semibold text-gray-500">Component Name</Label>
                                <Input
                                  type="text"
                                  value={component.name}
                                  onChange={(e) => {
                                    const updatedRecords = [...salaryRecords];
                                    const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                                    updatedRecords[recordIndex].customComponents[compIndex].name = e.target.value;
                                    setSalaryRecords(updatedRecords);
                                  }}
                                  className="mt-1"
                                  placeholder="e.g., Performance Bonus, Night Shift Allowance, etc."
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-semibold text-gray-500">Amount (₹)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={component.amount || ""}
                                  onChange={(e) => {
                                    const updatedRecords = [...salaryRecords];
                                    const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                                    updatedRecords[recordIndex].customComponents[compIndex].amount = Number(e.target.value);
                                    setSalaryRecords(updatedRecords);
                                  }}
                                  className="mt-1"
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updatedRecords = [...salaryRecords];
                                const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                                updatedRecords[recordIndex].customComponents = updatedRecords[recordIndex].customComponents.filter((_: any, i: number) => i !== compIndex);
                                setSalaryRecords(updatedRecords);
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-5"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Earnings + deductions summary — Gross & TDS live in the
                      same grid as EPF/ESIC/PT/LWF. Employer EPF is shown too
                      when it's part of CTC (it then reduces take-home). */}
                  <div className="mt-4">
                    <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(105px,1fr))]">
                        <div>
                          <div className="text-xs text-gray-500">Gross Monthly</div>
                          <div className="text-sm font-bold text-green-700">₹{fmt(salaryDetails.gross)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">EPF (EE)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.pfEE)}</div>
                        </div>
                        {pfFromCtc && (
                          <div>
                            <div className="text-xs text-gray-500">EPF (ER)</div>
                            <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.pfER)}</div>
                          </div>
                        )}
                        <div>
                          <div className="text-xs text-gray-500">ESIC (EE)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.esicEE)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Professional Tax</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.pt)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">LWF (EE)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.lwfEE)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">TDS (Monthly)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.tds)}</div>
                        </div>
                      </div>
                      {(record.customComponents && record.customComponents.length > 0) && (
                        <div className="mt-2 pt-2 border-t border-red-200">
                          <div className="text-xs text-gray-600">
                            Includes: {record.customComponents.map((c: any) => c.name).filter(n => n).join(", ")}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-blue-800">Net Pay (Take Home)</span>
                        <span className="text-xl font-bold text-blue-600">₹{fmt(Math.round(salaryDetails.net))}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
                </fieldset>
                )}
              </Card>
            );
          })}

          {/* Add Another Salary Component Button — lives with the
              detailed cards so it only appears in expanded mode. */}
          {showSalaryDetails && (
            <Button
              type="button"
              variant="outline"
              onClick={addSalaryRecord}
              className="w-full border-dashed border-2 py-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Another Salary Component
            </Button>
          )}

          {/* Statutory Settings Card */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-red-50 to-rose-50">
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-red-600" />
                Statutory Settings
              </CardTitle>
              <CardDescription>Configure statutory deductions applicable to this employee</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Declaration is FINAL once the employee saves it — there is no
                  admin unlock. To change an employee's salary/tax, add or edit a
                  salary component below (its FY follows the effective date). */}
              {employee && declarationLocked && (
                <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <span className="font-semibold">Declaration saved &amp; final.</span>{" "}
                  The employee has submitted their declaration and it can't be re-opened.
                  To change anything, add or edit a salary component below.
                </div>
              )}
              <div className="space-y-4">
                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={pfApplicable === true}
                      onChange={(e) => setPfApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">EPF Applicable (EE 12% + ER 12%)</label>
                    {pfOptOut && (
                      <span className="ml-2 rounded-full bg-red-100 text-red-700 text-[11px] font-semibold px-2.5 py-0.5">
                        Employee opted out of EPF
                      </span>
                    )}
                  </div>
                  {pfApplicable && (
                    <div className="flex gap-4 items-center ml-6 flex-wrap">
                      <select
                        className="border rounded p-1 text-sm"
                        value={pfBasis}
                        onChange={(e) => setPfBasis(e.target.value as "statutory" | "actual")}
                      >
                        <option value="statutory">Statutory (capped ₹15,000 basic)</option>
                        <option value="actual">Actual Basic Salary</option>
                      </select>
                      <div className="flex items-center gap-1">
                        <label className="text-sm text-gray-600 whitespace-nowrap">
                          Employee PF rate (%)
                        </label>
                        {pfRateLocked ? (
                          <>
                            <div className="border rounded p-1 text-sm w-20 bg-gray-100 text-gray-600 text-center">
                              {Math.max(12, pfEmployeeRate || 12)}%
                            </div>
                            <span className="text-[11px] text-gray-500 whitespace-nowrap">
                              Locked by employee for the year
                            </span>
                          </>
                        ) : (
                          <input
                            type="number"
                            min={12}
                            step={0.5}
                            value={pfEmployeeRate}
                            onChange={(e) => setPfEmployeeRate(Number(e.target.value))}
                            onBlur={(e) => {
                              if (Number(e.target.value) < 12) setPfEmployeeRate(12);
                            }}
                            className="border rounded p-1 text-sm w-20"
                          />
                        )}
                      </div>
                      <span className="text-xs text-gray-400">
                        Employer fixed at 12%.
                        {pfBasis === "statutory" && " Statutory basis caps the base at ₹15,000."}
                      </span>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={esicApplicable === true}
                      onChange={(e) => setEsicApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">ESIC Applicable (gross ≤ ₹21,000)</label>
                  </div>
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={ptApplicable === true}
                      onChange={(e) => setPtApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">Professional Tax</label>
                  </div>
                  {ptApplicable && (
                    <div className="flex gap-4 items-center ml-6 flex-wrap">
                      <select
                        className="border rounded p-1 text-sm"
                        value={ptState}
                        onChange={(e) => setPtState(e.target.value)}
                      >
                        {Object.entries(PT_STATES).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={lwfApplicable === true}
                      onChange={(e) => setLwfApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">Labour Welfare Fund (LWF)</label>
                  </div>
                  {lwfApplicable && (
                    <div className="flex gap-4 items-center ml-6 flex-wrap">
                      <select
                        className="border rounded p-1 text-sm"
                        value={lwfState}
                        onChange={(e) => setLwfState(e.target.value)}
                      >
                        {Object.entries(LWF_STATES).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={formData.tdsApplicable === true}
                      onChange={(e) => setFormData({ ...formData, tdsApplicable: e.target.checked })}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">Deduct TDS (Income Tax)</label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Retainer-ship Card */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-emerald-600" />
                Retainer-ship
              </CardTitle>
              <CardDescription>Defer part of monthly net pay into a pool, released as a lump sum at period end</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <label className="flex items-center gap-2 font-medium">
                <input type="checkbox" className="h-4 w-4 text-blue-600 rounded"
                  checked={retainerEnabled} onChange={(e) => setRetainerEnabled(e.target.checked)} />
                Enable retainer-ship for this employee
              </label>
              {retainerEnabled && (
                <>
                  <div className="grid grid-cols-2 gap-4 ml-6">
                    <div>
                      <label className="text-sm text-gray-600">Withhold per month (₹)</label>
                      <input type="number" min={0} className="border rounded p-1 text-sm w-full mt-1"
                        value={retainerAmount || ''} placeholder="0" onChange={(e) => setRetainerAmount(e.target.value === '' ? 0 : Number(e.target.value))} />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Period (months per cycle)</label>
                      <input type="number" min={1} className="border rounded p-1 text-sm w-full mt-1"
                        value={retainerPeriod || ''} placeholder="3" onChange={(e) => setRetainerPeriod(e.target.value === '' ? 0 : Number(e.target.value))} />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Start from (month)</label>
                      <input type="month" className="border rounded p-1 text-sm w-full mt-1"
                        value={retainerStart} onChange={(e) => setRetainerStart(e.target.value)} />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Take-home floor (₹)</label>
                      <input type="number" min={0} className="border rounded p-1 text-sm w-full mt-1"
                        value={retainerFloor || ''} placeholder="0" onChange={(e) => setRetainerFloor(e.target.value === '' ? 0 : Number(e.target.value))} />
                    </div>
                    <label className="flex items-center gap-2 mt-6 col-span-2">
                      <input type="checkbox" className="h-4 w-4 text-blue-600 rounded"
                        checked={retainerRecurring} onChange={(e) => setRetainerRecurring(e.target.checked)} />
                      Keep running — repeat every cycle (don't stop after one payout)
                    </label>
                  </div>
                  <div className="ml-6 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    {(() => {
                      const pool = retainerAmount * retainerPeriod;
                      const startLabel = retainerStart
                        ? new Date(retainerStart + '-01').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                        : 'the start month';
                      return `From ${startLabel}: withhold ₹${retainerAmount.toLocaleString('en-IN')}/mo for ${retainerPeriod} month(s); month ${retainerPeriod} pays the normal amount + ₹${pool.toLocaleString('en-IN')} pool${retainerRecurring ? ', then the cycle repeats and keeps running.' : ' (one-time, then stops).'}`;
                    })()}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* TDS Declaration Section - Only for Old Regime */}
          {formData.tdsApplicable && !isNewRegime && (
            <Card className="border-0 shadow-md">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
                <CardTitle className="text-lg">TDS Declaration (Form 12BB)</CardTitle>
                <CardDescription>Employee tax saving declarations for Old Regime</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80C (PPF/ELSS/LIC/EPF) - Max ₹1,50,000</Label>
                    <Input
                      type="number"
                      value={declaration.sec80c || ""}
                      onChange={(e) => setDeclaration({ ...declaration, sec80c: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80D (Health Insurance) - Max ₹50,000</Label>
                    <Input
                      type="number"
                      value={declaration.sec80d || ""}
                      onChange={(e) => setDeclaration({ ...declaration, sec80d: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80CCD(1B) (NPS Self) - Max ₹50,000</Label>
                    <Input
                      type="number"
                      value={declaration.sec80ccd1b || ""}
                      onChange={(e) => setDeclaration({ ...declaration, sec80ccd1b: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">HRA Exemption (Annual)</Label>
                    <Input
                      type="number"
                      value={declaration.hraExempt || ""}
                      onChange={(e) => setDeclaration({ ...declaration, hraExempt: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Home Loan Interest (Sec 24b) - Max ₹2,00,000</Label>
                    <Input
                      type="number"
                      value={declaration.homeLoan || ""}
                      onChange={(e) => setDeclaration({ ...declaration, homeLoan: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">LTA Exemption (Annual)</Label>
                    <Input
                      type="number"
                      value={declaration.ltaExempt || ""}
                      onChange={(e) => setDeclaration({ ...declaration, ltaExempt: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80G (Donations)</Label>
                    <Input
                      type="number"
                      value={declaration.sec80g || ""}
                      onChange={(e) => setDeclaration({ ...declaration, sec80g: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Employer NPS (Sec 80CCD(2)) - Monthly</Label>
                    <Input
                      type="number"
                      value={declaration.npsEmployer || ""}
                      onChange={(e) => setDeclaration({ ...declaration, npsEmployer: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* New Regime Note */}
          {formData.tdsApplicable && isNewRegime && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="font-semibold text-blue-800 text-sm mb-1">New Regime Benefits</div>
              <div className="text-xs text-blue-700">
                • Standard Deduction: ₹75,000<br />
                • Rebate u/s 87A: Up to ₹60,000 for income ≤ ₹12L (effectively zero tax up to ₹12.75L)<br />
                • Only Employer NPS (80CCD(2)) is deductible<br />
                • No other deductions (80C, 80D, HRA, etc.) are allowed
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back: {prevStepLabel}
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              Next: {nextStepLabel}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Upload Documents Tab — all uploads are OPTIONAL */}
      {activeTab === "documents" && (
        <div className="space-y-6">
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Upload Documents
                <span className="text-xs font-normal text-gray-500 ml-2">(Optional)</span>
              </CardTitle>
              <CardDescription>

                {!employee?.id}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {([
                  { key: "aadhar",            label: "Aadhar Card" },
                  { key: "pan",               label: "PAN Card" },
                  { key: "cv",                label: "CV / Resume" },
                  { key: "relieving_letter",  label: "Relieving Letter" },
                  { key: "form16",            label: "Form 16" },
                  { key: "experience_letter", label: "Experience Letter" },
                ] as { key: EmployeeDocType; label: string }[]).map((doc) => {
                  const state = docsState[doc.key];
                  const uploaded = !!state?.blob_path;
                  const staged = !!pendingDocs[doc.key];
                  const path = state?.public_url || state?.blob_path || "";
                  const ext = path.split("?")[0].split(".").pop()?.toLowerCase() || "";
                  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext);
                  const isPdf = ext === "pdf";
                  return (
                    <DocUploadCard
                      key={doc.key}
                      doc={doc}
                      state={state}
                      staged={staged}
                      uploaded={uploaded}
                      isImage={isImage}
                      isPdf={isPdf}
                      pendingName={pendingDocs[doc.key]?.name}
                      onUpload={(file) => handleUploadDoc(doc.key, file)}
                      onDelete={() => handleDeleteDoc(doc.key)}
                    />
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back: {prevStepLabel}
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
            >
              Next: {nextStepLabel}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Summary Tab */}
      {activeTab === "summary" && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-800">Review Employee Information</h2>
          <p className="text-gray-600">Please review all details before {employee?.id ? "updating" : "creating"} the employee</p>

          {/* Personal Summary */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-lg">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Full Name</Label>
                  <p className="text-gray-900 mt-1">{[formData.first_name, formData.middle_name, formData.last_name].filter(Boolean).join(" ")}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Employee ID</Label>
                  <p className="text-gray-900 mt-1 font-mono">{formData.employeeID}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Email</Label>
                  <p className="text-gray-900 mt-1">{formData.email}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Phone</Label>
                  <p className="text-gray-900 mt-1">{formData.phone || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Department</Label>
                  <p className="text-gray-900 mt-1">{getDepartmentName(formData.department_id)}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Role</Label>
                  <p className="text-gray-900 mt-1 capitalize">{formData.role}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Designation</Label>
                  <p className="text-gray-900 mt-1">{getDesignationName(formData.designation_id)}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Date of Joining</Label>
                  <p className="text-gray-900 mt-1">{formData.hire_date}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Date of Birth</Label>
                  <p className="text-gray-900 mt-1">{formData.date_of_birth || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Mode of Working</Label>
                  <p className="text-gray-900 mt-1 capitalize">{formData.modeOfWorking}</p>
                </div>
                {formData.manager_id && (
                  <div>
                    <Label className="text-xs font-semibold text-gray-500">Reporting Person</Label>
                    <p className="text-gray-900 mt-1">{getManagerName(formData.manager_id)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Address Summary */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="text-lg">Address Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Current Address</h4>
                  <p className="text-gray-600">{formData.current_address1}</p>
                  {formData.current_address2 && <p className="text-gray-600">{formData.current_address2}</p>}
                  <p className="text-gray-600">{formData.current_city}, {getStateName(formData.current_state)} - {formData.current_pin}</p>
                  <p className="text-gray-600">{getCountryName(formData.country)}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Permanent Address</h4>
                  {formData.sameAs ? (
                    <p className="text-gray-500 italic">Same as current address</p>
                  ) : (
                    <>
                      <p className="text-gray-600">{formData.permanent_address1}</p>
                      {formData.permanent_address2 && <p className="text-gray-600">{formData.permanent_address2}</p>}
                      <p className="text-gray-600">{formData.permanent_city}, {getStateName(formData.permanent_state)} - {formData.permanent_pin}</p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Salary Summary - Show all records */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="text-lg">Salary Details</CardTitle>
              <CardDescription>Tax Regime: {formData.taxRegime === 'new' ? 'New Regime' : 'Old Regime'}</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {salaryRecords.map((record, idx) => {
                const details = calculateSalaryDetailsForRecord(record);
                // Use the one FY-wide monthly TDS (not the per-record full-year
                // value) and re-derive total deductions / net to match.
                const fyTds = fyTax.monthlyTds;
                const totDedFy = details.totDed - details.tds + fyTds;
                const netFy = details.net + details.tds - fyTds;
                const endDate = recordEndDates[record.id] || null;
                return (
                  <div key={record.id} className={`${idx > 0 ? 'mt-6 pt-6 border-t border-gray-200' : ''}`}>
                    <div className="mb-3 flex justify-between items-center">
                      <h4 className="font-semibold text-gray-700">Salary Component {idx + 1}</h4>
                      <div className="flex gap-3 text-xs">
                        <span className="text-gray-500">
                          Effective: {record.effectiveDate}
                          {endDate ? ` → ${toISODate(endDate)}` : ' → present'}
                        </span>
                        {record.ctc > 0 && (
                          <span className="text-gray-500">CTC: ₹{fmt(record.ctc)}</span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs ${record.status === 'active' ? 'bg-green-100 text-green-700' : record.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                          {record.status}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-3">Earnings</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span>Basic Salary:</span><span>₹{fmt(record.basicSalary)}</span></div>
                          <div className="flex justify-between"><span>HRA:</span><span>₹{fmt(record.hra)}</span></div>
                          <div className="flex justify-between"><span>Conveyance:</span><span>₹{fmt(record.conveyance)}</span></div>
                          <div className="flex justify-between"><span>Medical Allowance:</span><span>₹{fmt(record.medicalallowance)}</span></div>
                          {record.mealAllowance > 0 && <div className="flex justify-between"><span>Meal Allowance / Food Coupons:</span><span>₹{fmt(record.mealAllowance)}</span></div>}
                          <div className="flex justify-between"><span>Special Allowance:</span><span>₹{fmt(record.allowance)}</span></div>
                          <div className="flex justify-between"><span>LTA:</span><span>₹{fmt(record.lta)}</span></div>
                          {record.others > 0 && <div className="flex justify-between"><span>Others:</span><span>₹{fmt(record.others)}</span></div>}
                          <div className="flex justify-between pt-2 border-t mt-2 font-semibold"><span>Gross Monthly:</span><span>₹{fmt(details.gross)}</span></div>
                        </div>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-red-800 mb-3">Deductions</h4>
                        <div className="space-y-2 text-sm">
                          {pfApplicable && <div className="flex justify-between"><span>EPF (EE):</span><span>₹{fmt(details.pfEE)}</span></div>}
                          {esicApplicable && details.esicEE > 0 && <div className="flex justify-between"><span>ESIC (EE):</span><span>₹{fmt(details.esicEE)}</span></div>}
                          {ptApplicable && <div className="flex justify-between"><span>Professional Tax:</span><span>₹{fmt(details.pt)}</span></div>}
                          {lwfApplicable && <div className="flex justify-between"><span>LWF (EE):</span><span>₹{fmt(details.lwfEE)}</span></div>}
                          {formData.tdsApplicable && <div className="flex justify-between"><span>TDS (FY prorated):</span><span>₹{fmt(fyTds)}</span></div>}
                          <div className="flex justify-between pt-2 border-t mt-2 font-semibold"><span>Total Deductions:</span><span>₹{fmt(totDedFy)}</span></div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-blue-800">Net Pay (Take Home)</span>
                        <span className="text-2xl font-bold text-blue-600">₹{fmt(netFy)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back: {prevStepLabel}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {employee?.id ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {employee?.id ? "Update Employee" : "Create Employee"}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Add Department Modal */}
      <Dialog open={addDepartmentModal} onOpenChange={setAddDepartmentModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Add New Department</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="departmentName">Department Name</Label>
            <Input
              id="departmentName"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setAddDepartmentModal(false)} variant="outline">
              Close
            </Button>
            <Button onClick={handleAddDepartment}>
              Add Department
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Designation Modal */}
      <Dialog open={addDesignationModal} onOpenChange={setAddDesignationModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Add New Designation</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="designationName">Designation Name</Label>
            <Input
              id="designationName"
              value={designationName}
              onChange={(e) => setDesignationName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setAddDesignationModal(false)} variant="outline">
              Close
            </Button>
            <Button onClick={handleAddDesignation}>
              Add Designation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Office-location missing popup — shown when the user picks
          "In Office" mode but the configured office row has no
          latitude / longitude / radius yet. They're redirected to the
          Office settings page to fill those in; once saved, picking
          "In Office" succeeds silently. */}
      <Dialog
        open={officeLocationMissingOpen}
        onOpenChange={setOfficeLocationMissingOpen}
      >
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-amber-600" />
              Set office location first
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-600 space-y-2">
            <p>
              In-office attendance needs the office's{" "}
              <span className="font-semibold">latitude</span>,{" "}
              <span className="font-semibold">longitude</span>, and{" "}
              <span className="font-semibold">radius</span> to verify
              employees are on premises.
            </p>
            <p>
              Please add these in Office Information first.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOfficeLocationMissingOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setOfficeLocationMissingOpen(false);
                navigate("/office");
              }}
            >
              Open Office Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}

